<template>
  <v-container fluid class="pa-0 pa-md-4 py-12 py-md-4">
    <v-card>
      <v-card-title class="edit-vehicle-header">
        <div class="d-flex flex-column flex-grow-1 ga-1 min-width-0 w-100">
          <div
            class="d-flex align-center flex-wrap ga-2"
            data-testid="edit-vehicle-title-row"
          >
            <v-btn icon="mdi-arrow-left" variant="text" @click="goBack" />
            <div class="text-h5 font-weight-bold flex-shrink-0" data-testid="edit-vehicle-title">
              Edit Vehicle
            </div>
            <div
              v-if="vehicleData.vin && !isLoadingVehicle"
              class="d-flex flex-wrap align-center ga-2 edit-vehicle-identity-badges"
              data-testid="edit-vehicle-identity-badges"
            >
              <v-chip
                v-if="vehicleSourceBadgeLabel"
                size="small"
                :color="isFeedBackedVehicle ? 'primary' : 'default'"
                variant="tonal"
                data-testid="edit-vehicle-source-badge"
              >
                {{ vehicleSourceBadgeLabel }}
              </v-chip>
              <v-chip size="small" variant="tonal" data-testid="edit-vehicle-vin-badge">
                {{ headerVinLabel }}
              </v-chip>
              <v-chip size="small" variant="tonal" data-testid="edit-vehicle-stock-badge">
                {{ headerStockNumberLabel }}
              </v-chip>
            </div>
            <v-btn
              v-if="feedType === 'manual' && vehicleData.vin && !isLoadingVehicle"
              type="button"
              variant="outlined"
              size="small"
              class="text-none ml-auto flex-shrink-0"
              data-testid="edit-vehicle-enhance-data"
              @click="enhanceDataDialogOpen = true"
            >
              Enhance data
            </v-btn>
          </div>
          <div
            v-if="headerHelperText"
            class="text-body-2 text-grey edit-vehicle-header-helper"
            data-testid="edit-vehicle-header-helper"
          >
            {{ headerHelperText }}
          </div>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Loading State -->
        <div v-if="isLoadingVehicle" class="pa-8 text-center">
          <v-progress-circular indeterminate color="primary" size="64" />
          <div class="mt-4">Loading vehicle data...</div>
        </div>

        <!-- Vehicle Form -->
        <div v-else-if="vehicleData.vin" class="pa-4">
          <v-form ref="formRef">
            <v-row class="edit-vehicle-sections-row ga-y-7">
              <!-- Dynamic Fields -->
              <v-col v-if="isLoadingFields" cols="12" class="text-center py-8">
                <v-progress-circular indeterminate color="primary" />
                <div class="mt-4">Loading vehicle fields...</div>
              </v-col>

              <template v-else>
                <v-col cols="12">
                  <EditVehicleSectionCard
                    title="Inventory lifecycle"
                    :helper-text="lifecycleSectionHelper"
                    test-id="edit-vehicle-section-card-inventory-lifecycle"
                    title-test-id="edit-vehicle-inventory-lifecycle-heading"
                  >
                    <InventoryLifecycleOwnershipControls
                      :feed-backed="isFeedBackedVehicle"
                      :inventory-state="inventoryStateModel"
                      :inventory-state-mode="inventoryStateModeModel"
                      :inventory-usage-type="inventoryUsageTypeModel"
                      :inventory-usage-type-mode="inventoryUsageTypeModeModel"
                      :show-manual-website-visibility="isManualDraftVehicle"
                      :website-visibility="websiteVisibilityModel"
                      :website-visibility-reason="selectedWebsiteVisibilityReason"
                      :website-visibility-reason-notes="websiteVisibilityReasonNotes"
                      :website-visibility-field-errors="websiteVisibilityFieldErrors"
                      :automatic-inventory-state="
                        liveOperationalBaseline.inventoryStateMode === 'AUTO'
                          ? liveOperationalBaseline.inventoryState
                          : undefined
                      "
                      :automatic-inventory-usage-type="
                        liveOperationalBaseline.inventoryUsageTypeMode === 'AUTO'
                          ? liveOperationalBaseline.inventoryUsageType
                          : undefined
                      "
                      :baseline-inventory-state="liveOperationalBaseline.inventoryState"
                      :baseline-inventory-state-mode="liveOperationalBaseline.inventoryStateMode"
                      :inventory-state-pending-change-state="inventoryStatePendingChangeState"
                      :baseline-inventory-usage-type="liveOperationalBaseline.inventoryUsageType"
                      :baseline-inventory-usage-type-mode="liveOperationalBaseline.inventoryUsageTypeMode"
                      :inventory-usage-type-pending-change-state="inventoryUsageTypePendingChangeState"
                      :inventory-state-options="inventoryStateOptions"
                      :inventory-usage-type-options="inventoryUsageTypeOptions"
                      @update:inventory-state="inventoryStateModel = $event"
                      @update:inventory-state-mode="inventoryStateModeModel = $event"
                      @update:inventory-usage-type="inventoryUsageTypeModel = $event"
                      @update:inventory-usage-type-mode="inventoryUsageTypeModeModel = $event"
                      @update:website-visibility="websiteVisibilityModel = $event"
                      @update:website-visibility-reason="selectedWebsiteVisibilityReason = $event"
                      @update:website-visibility-reason-notes="websiteVisibilityReasonNotes = $event"
                      @clear-website-visibility-field-error="clearWebsiteVisibilityFieldError"
                    />
                  </EditVehicleSectionCard>
                </v-col>

                <!-- Dynamically grouped fields by section -->
                <template v-for="(section, sectionIndex) in fieldSections" :key="sectionIndex">
                  <v-col
                    v-if="
                      section.fields.length > 0 ||
                      section.label === 'Essential Information' ||
                      section.id === 'media_photos' ||
                      (section.id === 'pricing_mileage' && hasIgnoredPricingHubPriceOverride)
                    "
                    cols="12"
                  >
                    <EditVehicleSectionCard
                      :title="section.label"
                      :helper-text="sectionHelperText(section.label)"
                      :test-id="sectionCardTestId(section.label)"
                      :title-test-id="sectionTitleTestId(section.label)"
                    >
                      <template v-if="section.id === 'pricing_mileage' && showPricingHubPriceSummary">
                        <v-sheet
                          rounded="lg"
                          border
                          class="pa-3 mb-4"
                          data-testid="edit-vehicle-pricing-hub-price-summary"
                        >
                          <div
                            class="d-flex flex-column flex-md-row justify-space-between align-start ga-3"
                          >
                            <div>
                              <div class="text-caption text-medium-emphasis mb-1">
                                ISE price
                              </div>
                              <div
                                class="text-body-1 font-weight-medium"
                                data-testid="edit-vehicle-pricing-hub-price-value"
                              >
                                {{ pricingHubReferencePriceDisplay }}
                              </div>
                              <div class="text-body-2 text-medium-emphasis mt-1">
                                Shopper-facing price is managed in Pricing Hub. This ISE price is shown
                                for reference only.
                              </div>
                            </div>

                            <v-btn
                              v-if="pricingHubExternalUrl"
                              variant="outlined"
                              color="primary"
                              size="small"
                              class="text-none"
                              append-icon="mdi-open-in-new"
                              :href="pricingHubExternalUrl"
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="edit-vehicle-open-pricing-hub-link"
                            >
                              Open Pricing Hub
                            </v-btn>
                          </div>
                        </v-sheet>
                      </template>
                      <template v-if="section.id === 'pricing_mileage' && hasIgnoredPricingHubPriceOverride">
                        <v-alert
                          type="warning"
                          variant="tonal"
                          density="comfortable"
                          class="mb-4"
                          data-testid="edit-vehicle-ignored-price-override-alert"
                        >
                          This vehicle has older ISE price override(s). Pricing Hub now owns
                          shopper-facing price, so these ISE overrides are ignored. Release them to avoid
                          confusion.
                        </v-alert>
                        <v-sheet
                          v-if="hasIgnoredPricingHubPriceFieldOverride('price')"
                          rounded="lg"
                          border
                          class="pa-3 mt-3 mb-4"
                          data-testid="pricing-hub-ignored-price-override-controls"
                        >
                          <div class="text-caption text-medium-emphasis mb-1">Price override</div>
                          <ManualOverrideFieldControls
                            :field-state="getFieldOverrideState('price')"
                            :release-policy="getFieldReleasePolicy('price')"
                            :expires-at="getFieldExpiresAt('price')"
                            :dealer-time-zone="dealerTimeZone"
                            :expanded="shouldExpandOverrideControls('price')"
                            @update:release-policy="setFieldReleasePolicy('price', $event)"
                            @update:expires-at="setFieldExpiresAt('price', $event)"
                            @toggle-removal="toggleOverrideRemoval('price')"
                            @revert-pending="revertPendingOverride('price')"
                            @revert-field="revertOverrideFieldChanges('price')"
                          />
                        </v-sheet>
                        <v-sheet
                          v-if="hasIgnoredPricingHubPriceFieldOverride('display_price')"
                          rounded="lg"
                          border
                          class="pa-3 mt-3 mb-4"
                          data-testid="pricing-hub-ignored-display-price-override-controls"
                        >
                          <div class="text-caption text-medium-emphasis mb-1">Display price override</div>
                          <ManualOverrideFieldControls
                            :field-state="getFieldOverrideState('display_price')"
                            :release-policy="getFieldReleasePolicy('display_price')"
                            :expires-at="getFieldExpiresAt('display_price')"
                            :dealer-time-zone="dealerTimeZone"
                            :expanded="shouldExpandOverrideControls('display_price')"
                            @update:release-policy="setFieldReleasePolicy('display_price', $event)"
                            @update:expires-at="setFieldExpiresAt('display_price', $event)"
                            @toggle-removal="toggleOverrideRemoval('display_price')"
                            @revert-pending="revertPendingOverride('display_price')"
                            @revert-field="revertOverrideFieldChanges('display_price')"
                          />
                        </v-sheet>
                      </template>
                      <template v-if="section.id === 'media_photos'">
                          <div
                            v-if="isFeedBackedVehicle"
                            class="d-flex flex-wrap ga-2 mb-4"
                            data-testid="edit-vehicle-photo-source-modes"
                          >
                            <v-btn
                              :variant="photoSourceMode === 'feed' ? 'flat' : 'outlined'"
                              :color="photoSourceMode === 'feed' ? 'primary' : undefined"
                              prepend-icon="mdi-database"
                              type="button"
                              data-testid="edit-vehicle-photo-mode-feed"
                              @click="setPhotoSourceMode('feed')"
                            >
                              Feed photos
                            </v-btn>
                            <v-btn
                              :variant="photoSourceMode === 'uploaded' ? 'flat' : 'outlined'"
                              :color="photoSourceMode === 'uploaded' ? 'primary' : undefined"
                              prepend-icon="mdi-cloud-upload"
                              type="button"
                              data-testid="edit-vehicle-photo-mode-uploaded"
                              @click="setPhotoSourceMode('uploaded')"
                            >
                              Uploaded photos
                            </v-btn>
                          </div>

                          <div
                            v-if="!isFeedBackedVehicle || photoSourceMode === 'uploaded'"
                            class="d-flex justify-end mb-3"
                          >
                            <v-menu>
                              <template #activator="{ props: menuProps }">
                                <v-btn
                                  variant="outlined"
                                  color="primary"
                                  type="button"
                                  v-bind="menuProps"
                                >
                                  <v-icon start icon="mdi-plus" />
                                  Add Photos
                                </v-btn>
                              </template>
                              <v-list density="compact">
                                <v-list-item
                                  prepend-icon="mdi-folder"
                                  title="Choose from Files"
                                  @click="triggerPhotoSelection"
                                />
                                <v-list-item
                                  prepend-icon="mdi-camera"
                                  title="Take Photo"
                                  @click="openCameraDialog"
                                />
                              </v-list>
                            </v-menu>
                          </div>

                          <input
                            ref="photoInputRef"
                            type="file"
                            accept="image/*"
                            multiple
                            class="d-none"
                            @change="handlePhotoSelection"
                          />
                          <CameraCaptureDialog
                            v-model="cameraDialogOpen"
                            @capture="handleCameraCapture"
                          />

                          <div
                            v-if="
                              (!isFeedBackedVehicle || photoSourceMode === 'uploaded') &&
                              photoItems.length === 0
                            "
                            class="d-flex align-center justify-center bg-grey-lighten-4 pa-6 text-center rounded"
                          >
                            <div>
                              <v-icon icon="mdi-image-multiple" size="48" color="grey" class="mb-2" />
                              <div class="text-body-2 text-grey">
                                No photos selected yet. You can upload multiple images. Name is optional
                                (base name only) — we'll keep the original extension and generate a name if
                                left blank. Drag and drop thumbnails to reorder.
                              </div>
                            </div>
                          </div>
                          <div
                            v-else-if="!isFeedBackedVehicle || photoSourceMode === 'uploaded'"
                          >
                            <div class="text-caption text-grey mb-2">Drag thumbnails to reorder</div>
                            <Draggable
                              v-model="photoItems"
                              item-key="id"
                              handle=".drag-handle"
                              tag="div"
                              class="photo-grid"
                              :animation="150"
                              ghost-class="drag-ghost"
                              chosen-class="drag-chosen"
                            >
                              <template #item="{ element: item, index }">
                                <div class="photo-thumb">
                                  <div
                                    class="photo-thumb-image"
                                    role="button"
                                    tabindex="0"
                                    :aria-label="`Open photo ${index + 1} preview`"
                                    @click="openPhotoPreview(item.kind === 'existing' ? item.url : item.previewUrl)"
                                    @keydown.enter.prevent="
                                      openPhotoPreview(item.kind === 'existing' ? item.url : item.previewUrl)
                                    "
                                    @keydown.space.prevent="
                                      openPhotoPreview(item.kind === 'existing' ? item.url : item.previewUrl)
                                    "
                                  >
                                    <v-img
                                      :src="item.kind === 'existing' ? item.url : item.previewUrl"
                                      :alt="`Photo ${index + 1}`"
                                      cover
                                      aspect-ratio="4/3"
                                      class="rounded-lg border"
                                    />
                                  </div>

                                  <button
                                    type="button"
                                    class="photo-drag-handle drag-handle"
                                    :aria-label="`Drag photo ${index + 1} to reorder`"
                                    title="Drag to reorder"
                                    @click.stop
                                  >
                                    <v-icon icon="mdi-drag" size="18" color="grey-darken-2" />
                                  </button>

                                  <v-btn
                                    icon="mdi-close"
                                    size="x-small"
                                    variant="flat"
                                    color="error"
                                    class="photo-remove"
                                    @click="removePhotoItem(item.id)"
                                  />

                                  <div v-if="item.kind === 'new'" class="mt-2">
                                    <v-text-field
                                      v-model="item.name"
                                      variant="outlined"
                                      density="compact"
                                      hide-details
                                      :placeholder="`Optional name (e.g. front)`"
                                      :suffix="item.ext ? '.' + item.ext : undefined"
                                    />
                                  </div>
                                </div>
                              </template>
                            </Draggable>
                          </div>

                          <div
                            v-if="isFeedBackedVehicle && shouldShowOverrideControls('dealer_photos')"
                            class="manual-override-field-container mt-3"
                            :class="manualOverrideFieldContainerClass('dealer_photos')"
                            :data-state="manualOverrideFieldContainerState('dealer_photos')"
                            :aria-label="manualOverrideFieldContainerLabel('dealer_photos')"
                            data-testid="edit-vehicle-photos-override"
                          >
                            <ManualOverrideFieldControls
                              :field-state="getFieldOverrideState('dealer_photos')"
                              :release-policy="getFieldReleasePolicy('dealer_photos')"
                              :expires-at="getFieldExpiresAt('dealer_photos')"
                              :dealer-time-zone="dealerTimeZone"
                              :expanded="shouldExpandOverrideControls('dealer_photos')"
                              @update:release-policy="setFieldReleasePolicy('dealer_photos', $event)"
                              @update:expires-at="setFieldExpiresAt('dealer_photos', $event)"
                              @toggle-removal="toggleOverrideRemoval('dealer_photos')"
                              @revert-pending="revertPendingOverride('dealer_photos')"
                              @revert-field="revertOverrideFieldChanges('dealer_photos')"
                            />
                          </div>

                          <div
                            v-if="!isFeedBackedVehicle || photoSourceMode === 'uploaded'"
                            class="mt-4"
                            data-testid="edit-vehicle-uploaded-library"
                          >
                            <div class="d-flex align-center justify-space-between mb-1">
                              <div class="text-subtitle-2">Previously uploaded photos</div>
                              <v-progress-circular
                                v-if="uploadedPhotoLibraryLoading"
                                indeterminate
                                size="18"
                                width="2"
                              />
                            </div>
                            <div class="text-caption text-medium-emphasis mb-2">
                              Select existing uploads to reuse them without uploading another copy.
                            </div>
                            <div
                              v-if="uploadedPhotoLibrary.length > 0"
                              class="photo-grid"
                              data-testid="edit-vehicle-uploaded-library-grid"
                            >
                              <button
                                v-for="image in uploadedPhotoLibrary"
                                :key="image.key"
                                type="button"
                                class="photo-thumb uploaded-library-photo"
                                :class="{ 'uploaded-library-photo--selected': isUploadedLibraryImageSelected(image.publicUrl) }"
                                :aria-pressed="isUploadedLibraryImageSelected(image.publicUrl)"
                                :title="image.fileName"
                                data-testid="edit-vehicle-uploaded-library-photo"
                                @click="toggleUploadedLibraryImage(image)"
                              >
                                <v-img
                                  :src="image.publicUrl"
                                  :alt="image.fileName"
                                  cover
                                  aspect-ratio="4/3"
                                  class="rounded-lg border"
                                />
                                <v-icon
                                  class="uploaded-library-photo-status"
                                  :icon="
                                    isUploadedLibraryImageSelected(image.publicUrl)
                                      ? 'mdi-check-circle'
                                      : 'mdi-plus-circle'
                                  "
                                  :color="
                                    isUploadedLibraryImageSelected(image.publicUrl)
                                      ? 'primary'
                                      : 'grey-darken-1'
                                  "
                                  size="24"
                                />
                              </button>
                            </div>
                            <div
                              v-else-if="!uploadedPhotoLibraryLoading"
                              class="text-caption text-medium-emphasis"
                              data-testid="edit-vehicle-uploaded-library-empty"
                            >
                              {{ uploadedPhotoLibraryError || 'No previous uploads found for this VIN.' }}
                            </div>
                          </div>

                          <div
                            v-if="isFeedBackedVehicle && photoSourceMode === 'feed'"
                            class="mt-4"
                            data-testid="edit-vehicle-feed-photos"
                          >
                            <div
                              class="text-subtitle-2 mb-1"
                              data-testid="edit-vehicle-feed-photos-label"
                            >
                              Feed photos (read-only)
                            </div>
                            <div class="text-caption text-medium-emphasis mb-2">
                              Saving this mode releases any uploaded-photo override. The mapped feed
                              photos, including configured fallbacks, will be used.
                            </div>
                            <div
                              v-if="feedPhotoUrls.length > 0"
                              class="photo-grid"
                              data-testid="edit-vehicle-feed-photos-grid"
                            >
                              <div
                                v-for="(url, index) in feedPhotoUrls"
                                :key="`feed_${index}_${url}`"
                                class="photo-thumb"
                                data-testid="edit-vehicle-feed-photo"
                              >
                                <div class="photo-thumb-image">
                                  <v-img
                                    :src="url"
                                    :alt="`Feed photo ${index + 1}`"
                                    cover
                                    aspect-ratio="4/3"
                                    class="rounded-lg border"
                                  />
                                </div>
                              </div>
                            </div>
                            <div
                              v-else
                              class="text-caption text-medium-emphasis"
                              data-testid="edit-vehicle-feed-photos-empty"
                            >
                              No feed photos.
                            </div>
                          </div>
                      </template>

                      <v-row
                        v-if="section.label === 'Essential Information'"
                        class="edit-vehicle-section-field-grid"
                        data-testid="edit-vehicle-essential-information-fields"
                      >
                        <v-col
                          cols="12"
                          md="6"
                          class="manual-override-field-container"
                          :class="manualOverrideFieldContainerClass('condition')"
                          :data-state="manualOverrideFieldContainerState('condition')"
                          :aria-label="manualOverrideFieldContainerLabel('condition')"
                          data-testid="edit-vehicle-essential-condition"
                        >
                          <div
                            class="edit-vehicle-field-block"
                            :class="manualOverrideFieldBlockClass('condition')"
                            data-testid="edit-vehicle-field-block-condition"
                          >
                            <v-select
                              ref="conditionFieldRef"
                              v-model="vehicleData.condition"
                              label="Condition *"
                              variant="outlined"
                              density="comfortable"
                              :items="conditionOptions"
                              :hide-details="!uiFieldErrors['condition']"
                              :error="Boolean(uiFieldErrors['condition'])"
                              :error-messages="
                                uiFieldErrors['condition'] ? [uiFieldErrors['condition']] : []
                              "
                              :disabled="isFeedBackedDisplayFieldDisabled('condition')"
                              @update:model-value="clearFieldError('condition')"
                            />
                            <ManualOverrideFieldControls
                              v-if="isFeedBackedVehicle && shouldShowOverrideControls('condition')"
                              :field-state="getFieldOverrideState('condition')"
                              :release-policy="getFieldReleasePolicy('condition')"
                              :expires-at="getFieldExpiresAt('condition')"
                              :dealer-time-zone="dealerTimeZone"
                              :expanded="shouldExpandOverrideControls('condition')"
                              @update:release-policy="setFieldReleasePolicy('condition', $event)"
                              @update:expires-at="setFieldExpiresAt('condition', $event)"
                              @toggle-removal="toggleOverrideRemoval('condition')"
                              @revert-pending="revertPendingOverride('condition')"
                              @revert-field="revertOverrideFieldChanges('condition')"
                            />
                          </div>
                        </v-col>
                        <v-col
                          cols="12"
                          md="6"
                          class="manual-override-field-container"
                          :class="manualOverrideFieldContainerClass('certified_by_dealer')"
                          :data-state="manualOverrideFieldContainerState('certified_by_dealer')"
                          :aria-label="manualOverrideFieldContainerLabel('certified_by_dealer')"
                          data-testid="edit-vehicle-essential-certified"
                        >
                          <template v-if="showCertifiedCheckbox">
                            <div
                              class="edit-vehicle-field-block"
                              :class="manualOverrideFieldBlockClass('certified_by_dealer')"
                              data-testid="edit-vehicle-field-block-certified_by_dealer"
                            >
                              <v-checkbox
                                v-model="isCertifiedUi"
                                label="Is Certified?"
                                density="comfortable"
                                :hide-details="!certifiedCheckboxHelperText"
                                :hint="certifiedCheckboxHelperText"
                                :persistent-hint="Boolean(certifiedCheckboxHelperText)"
                                :disabled="isCertifiedCheckboxDisabled"
                              />
                              <ManualOverrideFieldControls
                                v-if="
                                  isFeedBackedVehicle &&
                                  shouldShowOverrideControls('certified_by_dealer')
                                "
                                :field-state="getFieldOverrideState('certified_by_dealer')"
                                :release-policy="getFieldReleasePolicy('certified_by_dealer')"
                                :expires-at="getFieldExpiresAt('certified_by_dealer')"
                                :dealer-time-zone="dealerTimeZone"
                                :expanded="shouldExpandOverrideControls('certified_by_dealer')"
                                :conflict-message="certifiedNewConditionConflictMessage"
                                :suppress-actions="isCertifiedDependentOnConditionRelease"
                                @update:release-policy="
                                  setFieldReleasePolicy('certified_by_dealer', $event)
                                "
                                @update:expires-at="setFieldExpiresAt('certified_by_dealer', $event)"
                                @toggle-removal="toggleOverrideRemoval('certified_by_dealer')"
                                @revert-pending="revertPendingOverride('certified_by_dealer')"
                                @revert-field="revertOverrideFieldChanges('certified_by_dealer')"
                                @fix-conflict="focusConditionFieldForCertifiedConflict"
                              />
                            </div>
                          </template>
                        </v-col>
                        <v-col
                          cols="12"
                          class="manual-override-field-container"
                          :class="manualOverrideFieldContainerClass('conditions')"
                          :data-state="manualOverrideFieldContainerState('conditions')"
                          :aria-label="manualOverrideFieldContainerLabel('conditions')"
                          data-testid="edit-vehicle-essential-additional-conditions"
                        >
                          <div
                            class="edit-vehicle-field-block"
                            :class="manualOverrideFieldBlockClass('conditions')"
                            data-testid="edit-vehicle-field-block-conditions"
                          >
                            <v-combobox
                              v-model="additionalConditions"
                              label="Condition labels"
                              variant="outlined"
                              density="comfortable"
                              multiple
                              chips
                              closable-chips
                              clearable
                              hint='Add optional shopper-facing labels such as "One Owner" or "Nissan Certified."'
                              persistent-hint
                              :disabled="isFeedBackedDisplayFieldDisabled('conditions')"
                              @update:model-value="additionalConditionsTouched = true"
                            />
                            <ManualOverrideFieldControls
                              v-if="isFeedBackedVehicle && shouldShowOverrideControls('conditions')"
                              :field-state="getFieldOverrideState('conditions')"
                              :release-policy="getFieldReleasePolicy('conditions')"
                              :expires-at="getFieldExpiresAt('conditions')"
                              :dealer-time-zone="dealerTimeZone"
                              :expanded="shouldExpandOverrideControls('conditions')"
                              @update:release-policy="setFieldReleasePolicy('conditions', $event)"
                              @update:expires-at="setFieldExpiresAt('conditions', $event)"
                              @toggle-removal="toggleOverrideRemoval('conditions')"
                              @revert-pending="revertPendingOverride('conditions')"
                              @revert-field="revertOverrideFieldChanges('conditions')"
                            />
                          </div>
                        </v-col>
                      </v-row>

                      <v-row v-if="section.id !== 'media_photos'" class="edit-vehicle-section-field-grid">
                        <template v-for="field in section.fields" :key="field">
                          <v-col
                            v-if="shouldShowField(field)"
                            cols="12"
                            :md="getFieldInputType(field) === 'textarea' ? 12 : 6"
                            :class="manualOverrideFieldContainerClass(field)"
                            :data-state="manualOverrideFieldContainerState(field)"
                            :aria-label="manualOverrideFieldContainerLabel(field)"
                            class="manual-override-field-container"
                            :data-testid="`edit-vehicle-field-${field}`"
                          >
                            <div
                              class="edit-vehicle-field-block"
                              :class="manualOverrideFieldBlockClass(field)"
                              :data-testid="`edit-vehicle-field-block-${field}`"
                            >
                              <!-- Boolean/Checkbox fields -->
                              <v-checkbox
                                v-if="getFieldInputType(field) === 'boolean'"
                                :model-value="getBooleanFieldInputValue(field)"
                                :label="formatFieldLabel(field)"
                                density="comfortable"
                                hide-details
                                :disabled="isFeedBackedDisplayFieldDisabled(field)"
                                @update:model-value="setBooleanFieldInputValue(field, $event)"
                              />

                              <!-- Number fields -->
                              <v-text-field
                                v-else-if="getFieldInputType(field) === 'number'"
                                :model-value="getDraftFieldInputValue(field)"
                                :label="formatFieldLabel(field)"
                                variant="outlined"
                                density="comfortable"
                                type="number"
                                :min="getNumberConstraints(field)?.min"
                                :max="getNumberConstraints(field)?.max"
                                :step="getNumberConstraints(field)?.step"
                                :hint="getFieldHint(field)"
                                :persistent-hint="Boolean(getFieldHint(field))"
                                :hide-details="Boolean(getFieldHint(field)) ? false : !uiFieldErrors[field]"
                                :error="Boolean(uiFieldErrors[field])"
                                :error-messages="uiFieldErrors[field] ? [uiFieldErrors[field]] : []"
                                :disabled="isFeedBackedDisplayFieldDisabled(field)"
                                @update:model-value="setDraftFieldInputValue(field, $event)"
                                @update:focused="commitDraftFieldInputValueOnFocusChange(field, $event)"
                                @keydown.enter.prevent="commitDraftFieldInputValue(field)"
                                @keydown.esc.prevent="cancelDraftFieldInputValue(field)"
                              />

                              <!-- Date fields -->
                              <v-text-field
                                v-else-if="getFieldInputType(field) === 'date'"
                                :model-value="getDraftFieldInputValue(field)"
                                :label="formatFieldLabel(field)"
                                variant="outlined"
                                density="comfortable"
                                type="date"
                                clearable
                                :max="field === 'date_in_stock_ts' ? dateInStockMaxDate : undefined"
                                :hint="field === 'date_in_stock_ts' ? dateInStockFieldHelperText : undefined"
                                :persistent-hint="field === 'date_in_stock_ts'"
                                :hide-details="field === 'date_in_stock_ts' ? false : !uiFieldErrors[field]"
                                :error="Boolean(uiFieldErrors[field])"
                                :error-messages="uiFieldErrors[field] ? [uiFieldErrors[field]] : []"
                                :disabled="isFeedBackedDisplayFieldDisabled(field)"
                                @update:model-value="setDraftFieldInputValue(field, $event)"
                                @update:focused="commitDraftFieldInputValueOnFocusChange(field, $event)"
                                @keydown.enter.prevent="commitDraftFieldInputValue(field)"
                                @keydown.esc.prevent="cancelDraftFieldInputValue(field)"
                              />

                              <!-- Textarea fields -->
                              <v-textarea
                                v-else-if="getFieldInputType(field) === 'textarea'"
                                :model-value="getDraftFieldInputValue(field)"
                                :label="formatFieldLabel(field)"
                                variant="outlined"
                                density="comfortable"
                                rows="3"
                                auto-grow
                                :hide-details="!uiFieldErrors[field]"
                                :error="Boolean(uiFieldErrors[field])"
                                :error-messages="uiFieldErrors[field] ? [uiFieldErrors[field]] : []"
                                :disabled="isFeedBackedDisplayFieldDisabled(field)"
                                @update:model-value="setDraftFieldInputValue(field, $event)"
                                @update:focused="commitDraftFieldInputValueOnFocusChange(field, $event)"
                                @keydown.enter.prevent="commitDraftFieldInputValue(field)"
                                @keydown.esc.prevent="cancelDraftFieldInputValue(field)"
                              />

                              <!-- Select/Dropdown fields -->
                              <v-select
                                v-else-if="getFieldInputType(field) === 'select'"
                                v-model="vehicleData[field]"
                                :label="formatFieldLabel(field)"
                                variant="outlined"
                                density="comfortable"
                                :items="getDropdownOptions(field) || []"
                                clearable
                                :hide-details="!uiFieldErrors[field]"
                                :error="Boolean(uiFieldErrors[field])"
                                :error-messages="uiFieldErrors[field] ? [uiFieldErrors[field]] : []"
                                :disabled="isFeedBackedDisplayFieldDisabled(field)"
                                @update:model-value="clearFieldError(field)"
                              />

                              <!-- Default text fields -->
                              <v-text-field
                                v-else
                                :model-value="getDraftFieldInputValue(field)"
                                :label="formatFieldLabel(field)"
                                variant="outlined"
                                density="comfortable"
                                :hide-details="!uiFieldErrors[field]"
                                :error="Boolean(uiFieldErrors[field])"
                                :error-messages="uiFieldErrors[field] ? [uiFieldErrors[field]] : []"
                                :disabled="isFeedBackedDisplayFieldDisabled(field)"
                                @update:model-value="setDraftFieldInputValue(field, $event)"
                                @update:focused="commitDraftFieldInputValueOnFocusChange(field, $event)"
                                @keydown.enter.prevent="commitDraftFieldInputValue(field)"
                                @keydown.esc.prevent="cancelDraftFieldInputValue(field)"
                              />

                              <FieldEnhancementSourceMenu
                                v-if="feedType === 'manual' && isChromeEnhancementCandidateApplicable(field)"
                                :field="field"
                                :status="chromeEnhancementSourcePhase"
                                :source-label="CHROME_ENHANCEMENT_SOURCE.label"
                                :applicable="isChromeEnhancementCandidateApplicable(field)"
                                :candidate-value="chromeEnhancementCandidateDisplay(field)"
                                :notice="enhancementSourceNotices[field] || ''"
                                @open="loadChromeEnhancementSource()"
                                @retry="retryChromeEnhancementSource()"
                                @use="applyChromeEnhancementCandidate(field)"
                              />

                              <!-- Per-field manual override settings (feed-backed vehicles only) -->
                              <ManualOverrideFieldControls
                                v-if="isFeedBackedVehicle && shouldShowOverrideControls(field)"
                                :field-state="getFieldOverrideState(field)"
                                :release-policy="getFieldReleasePolicy(field)"
                                :expires-at="getFieldExpiresAt(field)"
                                :dealer-time-zone="dealerTimeZone"
                                :expanded="shouldExpandOverrideControls(field)"
                                @update:release-policy="setFieldReleasePolicy(field, $event)"
                                @update:expires-at="setFieldExpiresAt(field, $event)"
                                @toggle-removal="toggleOverrideRemoval(field)"
                                @revert-pending="revertPendingOverride(field)"
                                @revert-field="revertOverrideFieldChanges(field)"
                              />
                            </div>
                          </v-col>
                        </template>
                      </v-row>
                    </EditVehicleSectionCard>
                  </v-col>
                </template>
              </template>
            </v-row>

            <!-- Spacer so the last fields aren't hidden behind the fixed footer -->
            <div class="form-footer-spacer" />

            <!-- Sticky footer actions -->
            <div class="form-footer-sticky">
              <div class="form-footer-inner">
                <div class="d-flex ga-2">
                  <v-btn variant="outlined" @click="goBack">Cancel</v-btn>
                  <v-btn
                    color="primary"
                    :loading="isSaving"
                    :disabled="isSaving || isLoadingFields || !isFormValidForSave"
                    @click="saveVehicle"
                  >
                    {{ saveButtonLabel }}
                  </v-btn>
                </div>
              </div>
            </div>
          </v-form>
        </div>

        <!-- Error State -->
        <div v-else class="pa-8 text-center">
          <v-icon icon="mdi-alert-circle" size="64" color="error" class="mb-4" />
          <div class="text-h6 mb-2">Failed to Load Vehicle</div>
          <div class="text-body-2 text-grey mb-4">
            Unable to load vehicle data. Please return to the inventory list and try again.
          </div>
          <v-btn color="primary" variant="outlined" @click="goBack">Back to Inventory</v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-container>

  <!-- Photo preview dialog -->
  <v-dialog v-model="photoPreviewOpen" content-class="photo-preview-dialog">
    <v-card class="photo-preview-card">
      <v-btn
        icon="mdi-close"
        variant="flat"
        size="large"
        color="surface"
        class="photo-preview-close"
        aria-label="Close photo preview"
        @click="photoPreviewOpen = false"
      />
      <v-card-text class="photo-preview-body">
        <v-carousel
          v-if="photoPreviewSlides.length > 0"
          v-model="photoPreviewIndex"
          :cycle="false"
          :continuous="false"
          :show-arrows="photoPreviewSlides.length > 1"
          :hide-delimiters="photoPreviewSlides.length <= 1"
          height="100%"
        >
          <v-carousel-item v-for="(src, i) in photoPreviewSlides" :key="`${src}_${i}`">
            <div class="photo-preview-stage">
              <img :src="src" alt="Photo preview" class="photo-preview-img rounded" />
            </div>
          </v-carousel-item>
        </v-carousel>
      </v-card-text>
    </v-card>
  </v-dialog>

  <EnhanceVehicleDataDialog
    v-model="enhanceDataDialogOpen"
    :source-phase="chromeEnhancementSourcePhase"
    :reset-token="enhanceDataDialogResetToken"
    :review-changes="reviewBulkEnhancementChanges"
    :apply-changes="applyBulkEnhancementChanges"
    @retry="retryChromeEnhancementSource()"
  />
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { INVENTORY_STATE, INVENTORY_USAGE_TYPE, WEBSITE_VISIBILITY } from '@jazel-auto/ise-contracts/dist/inventory.js'
import type { WebsiteVisibility } from '@jazel-auto/ise-contracts/dist/inventory.js'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useAccountCapabilitiesStore } from '@/stores/accountCapabilities'
import { usePricingHubLaunch } from '@/composables/usePricingHubLaunch'
import { usePublishedReadyCourtesySetting } from '@/composables/usePublishedReadyCourtesySetting'
import { usePublishStore } from '@/stores/usePublishStore'
import {
  fetchVehicleByVin,
  fetchVehicleDraft,
  revertManualOverrideDraftAction,
  revertVehicleDraftField,
  type ActiveManualOverride,
  type InventoryOwnershipMode,
  type InventoryState,
  type InventoryUsageType,
  type ManualOverrideReleasePolicy,
  type PendingManualOverrideAction,
  type VehicleDraftIntent,
  type VehicleDraftRecord,
} from '@/services/inventory'
import { makeAlert } from '@/utils'
import { isExcludedFromSrpPatch } from '@/utils/a6LifecycleFields'
import Draggable from 'vuedraggable'
import CameraCaptureDialog from '@/components/CameraCaptureDialog.vue'
import {
  fetchIseMappingFields,
  extractIseMappingFieldKeys,
  extractFieldProvenanceMap,
  type FieldProvenanceMap,
} from '@/services/fieldMappingService'
import {
  buildManualOverrideActions,
  DEFAULT_OVERRIDE_RELEASE_POLICY,
  getDefaultReleasePolicyForField,
  type FieldOverrideSetting,
} from '@/utils/manualOverrideActions'
import {
  DEALER_PHOTOS_FIELD,
  feedPhotoUrlsForDisplay,
  resolveFeedBackedPhotoSaveIntent,
  seedFeedBackedPhotoUrls,
} from '@/utils/feedBackedPhotoOverride'
import InventoryLifecycleOwnershipControls from '@/components/InventoryLifecycleOwnershipControls.vue'
import EditVehicleSectionCard from '@/components/EditVehicleSectionCard.vue'
import FieldEnhancementSourceMenu from '@/components/FieldEnhancementSourceMenu.vue'
import EnhanceVehicleDataDialog from '@/components/EnhanceVehicleDataDialog.vue'
import ManualOverrideFieldControls from '@/components/ManualOverrideFieldControls.vue'
import '@/styles/edit-vehicle-field-blocks.css'
import {
  buildInventoryLifecycleOperationalPatch,
  normalizeOwnershipMode,
  type InventoryLifecycleSnapshot,
} from '@/utils/inventoryLifecycleOwnership'
import {
  applySavedPhotoUrlsToVehicle,
  buildFinalPhotoUrls,
  hasExistingPhotosChanged,
  toExistingPhotoItems,
  type ExistingPhotoOrderItem,
  type PhotoOrderItem,
} from '@/utils/photoOrdering'
import {
  DISPLAY_FIELD_ORDER,
  EDIT_VEHICLE_SECTIONS,
  EDIT_VEHICLE_SECTION_HELPERS,
  EDIT_VEHICLE_SPECS_EFFICIENCY_FIELD_LABELS,
  getEditVehicleDynamicFieldsForSection,
  getFieldInputType,
  isCuratedManualFillField,
  type EditVehicleSectionId,
} from '@/constants/vehicleFieldConfig'
import { getDropdownOptions, getNumberConstraints } from '@/constants/fieldOptions'
import { sanitizeVehicleRecordForDisplay } from '@/utils/vehicleFieldDisplay'
import {
  getCertifiedNewConditionConflictMessage,
  isCertifiedAllowedForCondition,
  shouldExcludeCertifiedOverrideDirty,
  validateCertifiedConditionOnSave,
} from '@/utils/certifiedConditionRules'
import {
  areFieldValuesEquivalent,
  buildFieldOverrideState,
  resolveCurrentBaseValue,
  resolveEffectiveInputValue,
  type FieldOverrideState,
} from '@/utils/fieldOverrideState'
import { buildCustomFeedUpdateFields } from '@/utils/vehicleDraftFieldPatch'
import { buildManualInventoryStateOptions } from '@/utils/manualInventoryStateOptions'
import {
  areDraftFieldValuesEquivalent,
  isVehicleDraftMeaningful,
  prunePatchNoOps,
  resolveFeedBackedFieldSaveIntent,
} from '@/utils/vehicleDraftCleanup'
import {
  dateInStockHelperText,
  dealerTodayDateInputValue,
  fromDealerDateInputValue,
  isFutureDealerDateInputValue,
  normalizeIntegerInput,
  toDealerDateInputValue,
} from '@/utils/vehicleFieldValueNormalization'
import { isVehicleHistoryBooleanField, normalizeBooleanLike } from '@/utils/vehicleHistoryBooleanFields'
import { isModelEField, packModelEValue } from '@/utils/editVehicleBooleanFields'
import { sanitizeAddVehicleEnhancementPrefill } from '@/utils/addVehicleEnhancementPrefill'
import {
  CHROME_ENHANCEMENT_SOURCE,
  formatPreparedEnhancementValue,
  prepareEditVehicleEnhancementCandidate,
  type EnhancementSourceLoadStatus,
} from '@/utils/editVehicleEnhancementSource'
import {
  buildEditVehicleBulkEnhancementReview,
  type BulkEnhancementApplyRequest,
  type BulkEnhancementFieldSnapshot,
  type BulkEnhancementMode,
  type BulkEnhancementReviewResult,
} from '@/utils/editVehicleBulkEnhancement'
import {
  pricingHubOwnsFeedBackedPrice as doesPricingHubOwnFeedBackedPrice,
  buildFeedBackedHiddenFieldIds,
  isFeedBackedPriceField,
} from '@/utils/editVehicleFeedBackedFieldVisibility'
import {
  buildManualWebsiteVisibilityBaseline,
  hasManualWebsiteVisibilityDiff,
  hydrateManualWebsiteVisibilityReason,
  normalizeManualWebsiteVisibility,
  readManualWebsiteVisibilityFromSources,
  validateManualWebsiteVisibilityForm,
  type ManualWebsiteVisibilityBaseline,
} from '@/utils/manualVehicleWebsiteVisibility'

const route = useRoute()
const router = useRouter()
const accountSettings = useAccountSettingsStore()
const accountCapabilities = useAccountCapabilitiesStore()
const pricingHubLaunch = usePricingHubLaunch()
const publishStore = usePublishStore()

const dealerTimeZone = computed(() => String(accountSettings.dealerTimeZone || '').trim())

const dateInStockFieldHelperText = computed(() => dateInStockHelperText(dealerTimeZone.value))
const dateInStockMaxDate = computed(() => dealerTodayDateInputValue(dealerTimeZone.value))

const vin = String(route.params.vin || '')
const isLoadingVehicle = ref(true)
const isLoadingFields = ref(false)
const isSaving = ref(false)
const feedType = ref<'manual' | 'auto' | null>(null)
const publishState = ref<'draft' | 'published'>('published')
const draftIntent = ref<VehicleDraftIntent | null>(null)

const isManualDraftVehicle = computed(() => {
  return feedType.value === 'manual' && publishState.value === 'draft'
})

// Feed-backed (auto) vehicles stage display-field edits as manual overrides on the VehicleDraft
// (committed at publish). Source-of-truth is feedType (CurrentInventory.type), never feedSources.
const isFeedBackedVehicle = computed(() => feedType.value === 'auto')

// Media / Photos is editable for feed-backed vehicles (dealer_photos manual override).
const FEED_BACKED_HIDDEN_SECTION_IDS = new Set<EditVehicleSectionId>(['features_equipment'])

const pricingHubOwnsCurrentFeedBackedPrice = computed(() =>
  doesPricingHubOwnFeedBackedPrice({
    hasLoaded: accountCapabilities.hasLoaded,
    pricingHubEnabled: accountCapabilities.pricingHubEnabled,
    loadError: accountCapabilities.loadError,
  }),
)

const feedBackedHiddenFieldIds = computed(() =>
  buildFeedBackedHiddenFieldIds({
    hasLoaded: accountCapabilities.hasLoaded,
    pricingHubEnabled: accountCapabilities.pricingHubEnabled,
    loadError: accountCapabilities.loadError,
  }),
)

const isPricingHubProtectedPriceField = (field: string): boolean =>
  isFeedBackedVehicle.value &&
  isFeedBackedPriceField(field) &&
  pricingHubOwnsCurrentFeedBackedPrice.value

const lifecycleSectionHelper = computed((): string | undefined =>
  isFeedBackedVehicle.value
    ? undefined
    : isManualDraftVehicle.value
      ? "Set the vehicle's inventory state, usage type, and website visibility after publish."
      : "Set the vehicle's inventory state and usage type.",
)

// Manual override staging context (feed-backed only).
const fieldProvenance = ref<FieldProvenanceMap>({})
const activeOverrides = ref<Record<string, ActiveManualOverride>>({})
const pendingOverrideActions = ref<Record<string, PendingManualOverrideAction>>({})
const overrideRemovals = ref<Set<string>>(new Set())

const hasIgnoredPricingHubPriceOverride = computed(() => {
  if (!isFeedBackedVehicle.value) return false
  if (!accountCapabilities.hasLoaded || !accountCapabilities.pricingHubEnabled) return false

  return (
    Boolean(activeOverrides.value.price) ||
    Boolean(pendingOverrideActions.value.price) ||
    Boolean(activeOverrides.value.display_price) ||
    Boolean(pendingOverrideActions.value.display_price)
  )
})

const hasIgnoredPricingHubPriceFieldOverride = (field: 'price' | 'display_price'): boolean => {
  if (!hasIgnoredPricingHubPriceOverride.value) return false
  return (
    Boolean(activeOverrides.value[field]) || Boolean(pendingOverrideActions.value[field])
  )
}

const showPricingHubPriceSummary = computed(
  () =>
    isFeedBackedVehicle.value &&
    accountCapabilities.hasLoaded &&
    accountCapabilities.pricingHubEnabled,
)

const pricingHubExternalUrl = computed(() => {
  if (!showPricingHubPriceSummary.value) return undefined

  const url = String(accountCapabilities.pricingHubUrl || '').trim()
  if (!url) return undefined

  // Price Inspector launch URL for the edited VIN carries the ISE session (INT-013);
  // fall back to the bare URL if launch inputs are missing.
  return pricingHubLaunch.inspectorUrlForVin(vin) ?? url
})

const draftFieldValues = ref<Record<string, unknown>>({})
const persistedVehicleBaseline = ref<Record<string, unknown>>({})
const loadedDraftFieldKeys = ref<Set<string>>(new Set())
/** Feed/base mapped values for display fields (never pending manualValue). */
const feedBaseValues = ref<Record<string, unknown>>({})

const pricingHubReferencePriceRaw = computed(() => {
  const feedBase = feedBaseValues.value.price
  if (feedBase != null) return feedBase

  const activePriceOverride = activeOverrides.value.price
  const releaseTarget =
    activePriceOverride?.valueIfReleasedNow ?? activePriceOverride?.currentFeedValue

  if (releaseTarget != null) return releaseTarget

  return (persistedVehicleBaseline.value as Record<string, unknown>)?.price
})

const pricingHubReferencePriceDisplay = computed(() => {
  const raw = pricingHubReferencePriceRaw.value
  const numeric =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw)
        : NaN

  if (!Number.isFinite(numeric)) return '—'

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(numeric)
})
/** Values loaded into the form on fetch/save/revert (pending > active > current). */
const loadedEffectiveValues = ref<Record<string, unknown>>({})
const loadedOverridePolicies = ref<
  Record<string, { releasePolicy: ManualOverrideReleasePolicy; expiresAt: string }>
>({})
/** Live operational ownership snapshot before any draft operationalPatch overlay is applied. */
const liveOperationalBaseline = ref<InventoryLifecycleSnapshot>({
  inventoryState: INVENTORY_STATE.UNKNOWN,
  inventoryStateMode: 'AUTO',
  inventoryUsageType: INVENTORY_USAGE_TYPE.RETAIL,
  inventoryUsageTypeMode: 'AUTO',
})
/** Saved draft/form snapshot after any VehicleDraft operationalPatch overlay has been applied. */
const loadedOperationalBaseline = ref<InventoryLifecycleSnapshot>({
  inventoryState: INVENTORY_STATE.UNKNOWN,
  inventoryStateMode: 'AUTO',
  inventoryUsageType: INVENTORY_USAGE_TYPE.RETAIL,
  inventoryUsageTypeMode: 'AUTO',
})
const websiteVisibilityModel = ref<WebsiteVisibility>(WEBSITE_VISIBILITY.VISIBLE)
const selectedWebsiteVisibilityReason = ref<string>('TEMP_HOLD')
const websiteVisibilityReasonNotes = ref<string>('')
const loadedWebsiteVisibilityBaseline = ref<ManualWebsiteVisibilityBaseline>({
  websiteVisibility: WEBSITE_VISIBILITY.VISIBLE,
})

// Per-field override settings are explicit user selections only (local datetime-local for expiresAt).
// Display + build fall back by precedence (explicit > active > pending > default) for untouched fields.
type FieldOverrideUiSetting = { releasePolicy: ManualOverrideReleasePolicy; expiresAt: string }
const overrideSettings = ref<Record<string, FieldOverrideUiSetting>>({})

const saveButtonLabel = computed(() => {
  return isManualDraftVehicle.value ? 'Save Draft Changes' : 'Save Changes'
})

/** Converts a local datetime-local input to an ISO string, or undefined when empty/invalid. */
const toIsoDateTime = (value: string): string | undefined => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return undefined
  const date = new Date(trimmed)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/** Converts an ISO datetime to the `datetime-local` input value (local time, no seconds/zone). */
const isoToLocalDateTimeInput = (iso: string | undefined): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** True when a feed-backed field has resolvable mapping provenance. */
const hasOverrideProvenance = (field: string): boolean => {
  return Boolean(fieldProvenance.value[field]?.resolvedSources?.[0])
}

/** True when a feed-backed field can currently be staged as a manual override action. */
const isOverrideEligible = (field: string): boolean => {
  if (isPricingHubProtectedPriceField(field)) return false
  return (
    // Photos are eligible with or without a photo mapping (provenance is never fabricated).
    field === DEALER_PHOTOS_FIELD ||
    isCuratedManualFillField(field) ||
    hasOverrideProvenance(field) ||
    Boolean(activeOverrides.value[field]) ||
    Boolean(pendingOverrideActions.value[field])
  )
}

/** Missing-provenance/value feed-backed display fields are read-only; operational fields do not use this. */
const isFeedBackedDisplayFieldDisabled = (field: string): boolean => {
  return isFeedBackedVehicle.value && !isOverrideEligible(field)
}

const hasDraftFieldInputValue = (field: string): boolean =>
  Object.prototype.hasOwnProperty.call(draftFieldValues.value, field)

const hasOwnField = (source: Record<string, unknown> | null | undefined, field: string): boolean =>
  Boolean(source && Object.prototype.hasOwnProperty.call(source, field))

const getDraftFieldInputValue = (field: string): unknown => {
  const raw = hasDraftFieldInputValue(field)
    ? draftFieldValues.value[field]
    : (vehicleData.value as Record<string, unknown>)?.[field]

  if (getFieldInputType(field) === 'date') {
    return toDealerDateInputValue(raw, dealerTimeZone.value)
  }

  return raw
}

const setDraftFieldInputValue = (field: string, value: unknown) => {
  if (isPricingHubProtectedPriceField(field)) return
  draftFieldValues.value = {
    ...draftFieldValues.value,
    [field]: value,
  }
  clearFieldError(field)
}

const getBooleanFieldInputValue = (field: string): boolean => {
  return normalizeBooleanLike((vehicleData.value as Record<string, unknown>)?.[field]) ?? false
}

const setBooleanFieldInputValue = (field: string, value: unknown) => {
  const vehicle = vehicleData.value as Record<string, unknown>
  const original = vehicle[field]
  vehicle[field] = isModelEField(field) ? packModelEValue(value, original) : Boolean(value)
  clearFieldError(field)
}

const hasVehicleHistoryFieldPresence = (field: string): boolean => {
  if (!isVehicleHistoryBooleanField(field)) return true
  if (isCuratedManualFillField(field)) return true

  const vehicle = vehicleData.value as Record<string, unknown>
  return (
    hasOwnField(vehicle, field) ||
    hasOverrideProvenance(field) ||
    Boolean(activeOverrides.value[field]) ||
    Boolean(pendingOverrideActions.value[field]) ||
    loadedDraftFieldKeys.value.has(field) ||
    hasDraftFieldInputValue(field) ||
    isFieldDirty(field)
  )
}

const getDraftFieldValidationError = (field: string, value: unknown): string => {
  if (field === 'date_in_stock_ts' && isFutureDealerDateInputValue(value, dealerTimeZone.value)) {
    return 'Date In Stock cannot be in the future.'
  }
  return ''
}

const normalizeCommittedDraftFieldValue = (field: string, value: unknown): unknown => {
  if (getFieldInputType(field) === 'date') {
    const original = (vehicleData.value as Record<string, unknown>)?.[field]
    return fromDealerDateInputValue(value, original, dealerTimeZone.value)
  }

  if (field === 'passenger_capacity') {
    return normalizeIntegerInput(value)
  }

  if (getFieldInputType(field) !== 'number') return value
  const parsed = parseFloat(String(value))
  return Number.isNaN(parsed) ? value : parsed
}

const clearPendingOverrideActionLocally = (field: string) => {
  if (!pendingOverrideActions.value[field]) return
  const nextPending = { ...pendingOverrideActions.value }
  delete nextPending[field]
  pendingOverrideActions.value = nextPending
}

const clearOverrideSettingsForField = (field: string) => {
  if (!overrideSettings.value[field]) return
  const nextSettings = { ...overrideSettings.value }
  delete nextSettings[field]
  overrideSettings.value = nextSettings
}

const clearNoOpFeedBackedCommit = (field: string, committedValue: unknown): boolean => {
  if (!isFeedBackedVehicle.value || activeOverrides.value[field]) return false

  const baseline = feedBaseValues.value[field]
  if (!areDraftFieldValuesEquivalent(field, committedValue, baseline)) return false

  const vehicle = vehicleData.value as Record<string, unknown>
  vehicle[field] = baseline
  loadedEffectiveValues.value = {
    ...loadedEffectiveValues.value,
    [field]: baseline,
  }
  clearOverrideSettingsForField(field)
  const hadPendingAction = Boolean(pendingOverrideActions.value[field])
  clearPendingOverrideActionLocally(field)

  if (hadPendingAction) {
    void removeManualOverrideDraftActions([field])
  }

  return true
}

const commitDraftFieldInputValue = (field: string) => {
  if (isPricingHubProtectedPriceField(field)) return
  if (!hasDraftFieldInputValue(field)) return
  const validationError = getDraftFieldValidationError(field, draftFieldValues.value[field])
  if (validationError) {
    fieldErrors.value = {
      ...fieldErrors.value,
      [field]: validationError,
    }
    return
  }
  const committedValue = normalizeCommittedDraftFieldValue(
    field,
    draftFieldValues.value[field],
  )
  ;(vehicleData.value as Record<string, unknown>)[field] = committedValue
  const next = { ...draftFieldValues.value }
  delete next[field]
  draftFieldValues.value = next
  clearFieldError(field)
  clearNoOpFeedBackedCommit(field, committedValue)
}

const commitDraftFieldInputValueOnFocusChange = (field: string, focused: boolean) => {
  if (!focused) commitDraftFieldInputValue(field)
}

const cancelDraftFieldInputValue = (field: string) => {
  if (!hasDraftFieldInputValue(field)) return
  const next = { ...draftFieldValues.value }
  delete next[field]
  draftFieldValues.value = next
  clearFieldError(field)
}

const commitAllDraftFieldInputValues = () => {
  for (const field of Object.keys(draftFieldValues.value)) {
    commitDraftFieldInputValue(field)
  }
}

/** True when the field's local value differs from the loaded effective value (unsaved edit). */
const isFieldDirty = (field: string): boolean => {
  if (
    shouldExcludeCertifiedOverrideDirty({
      isFeedBacked: isFeedBackedVehicle.value,
      field,
      condition: (vehicleData.value as Record<string, unknown>)?.condition,
    })
  ) {
    return false
  }
  if (isFeedBackedDisplayFieldDisabled(field)) return false
  if (isFeedBackedVehicle.value && field in loadedEffectiveValues.value) {
    const loaded = loadedEffectiveValues.value[field]
    const after = (vehicleData.value as Record<string, unknown>)?.[field]
    return !areDraftFieldValuesEquivalent(field, loaded, after)
  }
  const before = (draftBaseline.value as Record<string, unknown> | null)?.[field]
  const after = (vehicleData.value as Record<string, unknown>)?.[field]
  return !areDraftFieldValuesEquivalent(field, before, after)
}

/** Effective manualValue for policy-only saves when the form shows the feed/base value. */
const resolveEffectiveManualValue = (field: string): unknown => {
  if (isFieldDirty(field)) {
    return (vehicleData.value as Record<string, unknown>)?.[field]
  }
  const pending = pendingOverrideActions.value[field]
  if (pending && pending.action !== 'REMOVE_OVERRIDE' && 'manualValue' in pending) {
    return pending.manualValue
  }
  const active = activeOverrides.value[field]
  if (active) return active.manualValue
  return (vehicleData.value as Record<string, unknown>)?.[field]
}

const resolveActiveReleaseTarget = (field: string): unknown => {
  const active = activeOverrides.value[field]
  if (!active) return undefined
  if ('valueIfReleasedNow' in active && active.valueIfReleasedNow !== undefined) {
    return active.valueIfReleasedNow
  }
  if ('currentFeedValue' in active) return active.currentFeedValue
  return undefined
}

const hasAvailableActiveReleaseTarget = (field: string): boolean => {
  const active = activeOverrides.value[field]
  if (!active) return false
  if (active.releasePreviewStatus === 'unavailable') return false
  return resolveActiveReleaseTarget(field) !== undefined
}

const isLocalValueAtActiveReleaseTarget = (field: string): boolean => {
  if (!hasAvailableActiveReleaseTarget(field)) return false
  if (!(field in loadedEffectiveValues.value)) return false

  const localValue = (vehicleData.value as Record<string, unknown>)?.[field]
  const loadedValue = loadedEffectiveValues.value[field]
  if (areFieldValuesEquivalent(field, localValue, loadedValue)) return false

  return areFieldValuesEquivalent(field, localValue, resolveActiveReleaseTarget(field))
}

const isFieldReleaseStaged = (field: string): boolean => {
  return (
    pendingOverrideActions.value[field]?.action === 'REMOVE_OVERRIDE' ||
    overrideRemovals.value.has(field) ||
    isLocalValueAtActiveReleaseTarget(field)
  )
}

const collectLocallyReleasedOverrideFields = (fields: Iterable<string>): string[] => {
  return Array.from(fields).filter((field) => isLocalValueAtActiveReleaseTarget(field))
}

const collectFeedBackedOverrideFields = (): Set<string> => {
  const fields = [
    ...Object.values(EDIT_VEHICLE_SECTIONS)
      .filter(
        (section) =>
          section.id !== 'inventory_lifecycle' &&
          !FEED_BACKED_HIDDEN_SECTION_IDS.has(section.id),
      )
      .flatMap((section) => getEditVehicleDynamicFieldsForSection(section.id, availableFields.value))
      .filter(isCuratedManualFillField),
    ...Object.keys(fieldProvenance.value),
    ...Object.keys(activeOverrides.value),
    ...Object.keys(pendingOverrideActions.value),
    'condition',
    'certified_by_dealer',
    'conditions',
  ]

  return new Set(
    fields.filter(
      (field) =>
        !feedBackedHiddenFieldIds.value.has(field) ||
        Boolean(activeOverrides.value[field]) ||
        Boolean(pendingOverrideActions.value[field]),
    ),
  )
}

const syncFeedBackedFieldInputs = () => {
  if (!isFeedBackedVehicle.value) {
    feedBaseValues.value = {}
    loadedEffectiveValues.value = {}
    loadedOverridePolicies.value = {}
    return
  }

  const vehicle = vehicleData.value as Record<string, unknown>
  const bases: Record<string, unknown> = {}
  const loaded: Record<string, unknown> = {}
  const loadedPolicies: Record<
    string,
    { releasePolicy: ManualOverrideReleasePolicy; expiresAt: string }
  > = {}

  for (const field of collectFeedBackedOverrideFields()) {
    // dealer_photos is seeded below from the photo grid helpers (feed photos are never copied in).
    if (field === DEALER_PHOTOS_FIELD) continue
    const pending = pendingOverrideActions.value[field]
    const active = activeOverrides.value[field]
    const removalStaged =
      pending?.action === 'REMOVE_OVERRIDE' || overrideRemovals.value.has(field)

    const rawVehicleValue =
      field in feedBaseValues.value
        ? feedBaseValues.value[field]
        : isOverrideEligible(field) && vehicle[field] === undefined
          ? null
          : vehicle[field]
    const currentValue = resolveCurrentBaseValue({
      rawVehicleValue,
      activeOverride: active,
      pendingAction: pending,
    })
    bases[field] = currentValue

    const effectiveValue = resolveEffectiveInputValue({
      currentValue,
      activeOverride: active,
      pendingAction: pending,
      removalStaged,
    })
    loaded[field] = effectiveValue
    vehicle[field] = effectiveValue

    if (active || pending) {
      loadedPolicies[field] = {
        releasePolicy: getFieldReleasePolicy(field),
        expiresAt: getFieldExpiresAt(field),
      }
    }
  }

  // Photos: feed photos are the read-only base; the grid is seeded from the override state only.
  const photoPending = pendingOverrideActions.value[DEALER_PHOTOS_FIELD]
  const photoActive = activeOverrides.value[DEALER_PHOTOS_FIELD]
  const feedPhotos = Array.isArray(feedBaseValues.value[DEALER_PHOTOS_FIELD])
    ? (feedBaseValues.value[DEALER_PHOTOS_FIELD] as string[])
    : feedPhotoUrlsForDisplay({
        activeOverride: photoActive,
        pendingAction: photoPending,
        vehicleValue: (persistedVehicleBaseline.value as Record<string, unknown>)?.[DEALER_PHOTOS_FIELD],
      })
  const seededPhotos = seedFeedBackedPhotoUrls({
    activeOverride: photoActive,
    pendingAction: photoPending,
  })
  bases[DEALER_PHOTOS_FIELD] = feedPhotos
  loaded[DEALER_PHOTOS_FIELD] = seededPhotos
  vehicle[DEALER_PHOTOS_FIELD] = [...seededPhotos]
  if (photoActive || photoPending) {
    loadedPolicies[DEALER_PHOTOS_FIELD] = {
      releasePolicy: getFieldReleasePolicy(DEALER_PHOTOS_FIELD),
      expiresAt: getFieldExpiresAt(DEALER_PHOTOS_FIELD),
    }
  }
  clearNewPhotoPreviews()
  originalPhotoUrls.value = [...seededPhotos]
  photoItems.value = toExistingPhotoItems(seededPhotos)
  uploadedPhotoModeItems.value = [...photoItems.value]
  photoSourceMode.value = seededPhotos.length > 0 ? 'uploaded' : 'feed'

  feedBaseValues.value = bases
  loadedEffectiveValues.value = loaded
  loadedOverridePolicies.value = loadedPolicies
  draftFieldValues.value = {}
  syncDraftBaselineFromForm()
}

const getFieldOverrideState = (field: string): FieldOverrideState => {
  const vehicle = vehicleData.value as Record<string, unknown>
  const pending = pendingOverrideActions.value[field] ?? null
  const active = activeOverrides.value[field] ?? null
  const removalStaged = isFieldReleaseStaged(field)
  const vehicleHasField = hasOwnField(vehicle, field)
  const missingCuratedFeedValue =
    isCuratedManualFillField(field) &&
    !active &&
    !pending &&
    ((!vehicleHasField) || (field in feedBaseValues.value && feedBaseValues.value[field] == null))

  const currentValue =
    field in feedBaseValues.value
      ? feedBaseValues.value[field]
      : resolveCurrentBaseValue({
          rawVehicleValue: vehicle[field],
          activeOverride: active,
          pendingAction: pending,
        })

  const loadedEffectiveValue =
    field in loadedEffectiveValues.value
      ? loadedEffectiveValues.value[field]
      : resolveEffectiveInputValue({
          currentValue,
          activeOverride: active,
          pendingAction: pending,
          removalStaged,
        })

  return buildFieldOverrideState({
    field,
    currentValue,
    localValue: vehicle[field],
    loadedEffectiveValue,
    activeOverride: active,
    pendingAction: pending,
    provenanceMissing:
      field !== DEALER_PHOTOS_FIELD && (!hasOverrideProvenance(field) || missingCuratedFeedValue),
    allowMissingProvenance: isCuratedManualFillField(field) || field === DEALER_PHOTOS_FIELD,
    removalStaged,
    conflictMessage:
      field === 'certified_by_dealer' ? certifiedNewConditionConflictMessage.value : null,
    releasePolicy: getFieldReleasePolicy(field),
    expiresAt: getFieldExpiresAt(field),
    loadedReleasePolicy:
      loadedOverridePolicies.value[field]?.releasePolicy ?? active?.releasePolicy,
    loadedExpiresAt:
      loadedOverridePolicies.value[field]?.expiresAt ??
      isoToLocalDateTimeInput(active?.expiresAt),
    dealerTimeZone: dealerTimeZone.value,
  })
}

const syncDraftBaselineFromForm = () => {
  draftBaseline.value = JSON.parse(JSON.stringify(vehicleData.value || {}))
}

/** Show compact override row for every feed-backed display field (eligible or provenance-blocked). */
const shouldShowOverrideControls = (field: string): boolean => {
  if (!isFeedBackedVehicle.value) return false
  if (isFeedBackedPriceField(field) && pricingHubOwnsCurrentFeedBackedPrice.value) return false
  return (
    field === DEALER_PHOTOS_FIELD ||
    isCuratedManualFillField(field) ||
    hasOverrideProvenance(field) ||
    isFeedBackedDisplayFieldDisabled(field) ||
    Boolean(activeOverrides.value[field]) ||
    Boolean(pendingOverrideActions.value[field])
  )
}

const manualOverrideFieldContainerState = (field: string): 'normal' | 'unsaved' | 'pending' => {
  if (!isFeedBackedVehicle.value || !shouldShowOverrideControls(field)) return 'normal'

  const state = getFieldOverrideState(field)
  if (state.hasUnsavedLocalChange) {
    return 'unsaved'
  }

  if (state.hasPendingDraftAction) return 'pending'

  return 'normal'
}

const manualOverrideFieldContainerClass = (field: string): Record<string, boolean> => {
  const state = manualOverrideFieldContainerState(field)
  return {
    'manual-override-field-container--unsaved': state === 'unsaved',
    'manual-override-field-container--pending': state === 'pending',
  }
}

const manualOverrideFieldBlockClass = (field: string): Record<string, boolean> => {
  const state = manualOverrideFieldContainerState(field)
  return {
    'edit-vehicle-field-block--unsaved': state === 'unsaved',
    'edit-vehicle-field-block--pending': state === 'pending',
  }
}

const manualOverrideFieldContainerLabel = (field: string): string | undefined => {
  const state = manualOverrideFieldContainerState(field)
  if (state === 'unsaved') return 'Unsaved changes'
  if (state === 'pending') return 'Saved draft pending publish'
  return undefined
}

const isFieldUntilDateMissingExpiry = (field: string): boolean => {
  return (
    overrideSettings.value[field]?.releasePolicy === 'UNTIL_DATE' &&
    !String(getFieldExpiresAt(field) || '').trim()
  )
}

/** Auto-expand settings only for local unsaved edits or missing expiry validation. */
const shouldExpandOverrideControls = (field: string): boolean => {
  if (isFieldReleaseStaged(field)) return false
  if (isFieldUntilDateMissingExpiry(field)) return true
  if (!isFieldDirty(field)) return false
  if (activeOverrides.value[field]) return false

  const pending = pendingOverrideActions.value[field]
  if (pending && pending.action !== 'REMOVE_OVERRIDE') return false

  return isOverrideEligible(field)
}

const conditionFieldRef = ref<{ focus?: () => void } | null>(null)

const focusConditionFieldForCertifiedConflict = () => {
  conditionFieldRef.value?.focus?.()
  makeAlert('Set condition to Used to apply a certified override.', 'info')
}

/** Effective release policy for display: explicit > ACTIVE > pending > per-field product default. */
const getFieldReleasePolicy = (field: string): ManualOverrideReleasePolicy => {
  return (
    overrideSettings.value[field]?.releasePolicy ??
    activeOverrides.value[field]?.releasePolicy ??
    pendingOverrideActions.value[field]?.releasePolicy ??
    getDefaultReleasePolicyForField(field, DEFAULT_OVERRIDE_RELEASE_POLICY)
  )
}

/** Effective expiresAt for display (local datetime-local value), using the same precedence. */
const getFieldExpiresAt = (field: string): string => {
  const explicit = overrideSettings.value[field]?.expiresAt
  if (explicit !== undefined) return explicit
  return isoToLocalDateTimeInput(
    activeOverrides.value[field]?.expiresAt ?? pendingOverrideActions.value[field]?.expiresAt,
  )
}

const setFieldReleasePolicy = (field: string, releasePolicy: ManualOverrideReleasePolicy) => {
  overrideSettings.value = {
    ...overrideSettings.value,
    [field]: { releasePolicy, expiresAt: getFieldExpiresAt(field) },
  }
}

const setFieldExpiresAt = (field: string, expiresAt: string) => {
  overrideSettings.value = {
    ...overrideSettings.value,
    [field]: { releasePolicy: getFieldReleasePolicy(field), expiresAt },
  }
}

const toggleOverrideRemoval = (field: string) => {
  const next = new Set(overrideRemovals.value)
  if (next.has(field)) next.delete(field)
  else next.add(field)
  overrideRemovals.value = next
}

/** Resets the photo grid to the loaded override state (discarding unsaved uploads/reorders). */
const resetPhotoGridToLoaded = () => {
  const loadedPhotos = Array.isArray(loadedEffectiveValues.value[DEALER_PHOTOS_FIELD])
    ? (loadedEffectiveValues.value[DEALER_PHOTOS_FIELD] as string[])
    : []
  clearNewPhotoPreviews()
  originalPhotoUrls.value = [...loadedPhotos]
  photoItems.value = toExistingPhotoItems(loadedPhotos)
}

/** Reverts unsaved local edits for a single feed-backed override field (value, policy, or staged release). */
const revertLocalFieldChanges = (field: string) => {
  if (!isFeedBackedVehicle.value) return

  const vehicle = vehicleData.value as Record<string, unknown>
  cancelDraftFieldInputValue(field)

  if (field in loadedEffectiveValues.value) {
    vehicle[field] = loadedEffectiveValues.value[field]
  }

  if (field === DEALER_PHOTOS_FIELD) resetPhotoGridToLoaded()

  if (overrideSettings.value[field]) {
    const nextSettings = { ...overrideSettings.value }
    delete nextSettings[field]
    overrideSettings.value = nextSettings
  }

  if (overrideRemovals.value.has(field)) {
    const nextRemovals = new Set(overrideRemovals.value)
    nextRemovals.delete(field)
    overrideRemovals.value = nextRemovals
  }
}

const revertConditionReleaseWithDependentCertified = async () => {
  const shouldClearCertified = isCertifiedDependentOnConditionRelease.value
  if (shouldClearCertified) {
    if (pendingOverrideActions.value.certified_by_dealer?.action === 'REMOVE_OVERRIDE') {
      await discardPendingOverrideAction('certified_by_dealer')
    }
    if (overrideRemovals.value.has('certified_by_dealer')) {
      const nextRemovals = new Set(overrideRemovals.value)
      nextRemovals.delete('certified_by_dealer')
      overrideRemovals.value = nextRemovals
    }
  }

  if (pendingOverrideActions.value.condition?.action === 'REMOVE_OVERRIDE') {
    await discardPendingOverrideAction('condition')
    syncFeedBackedFieldInputs()
    makeAlert('Pending override change discarded', 'success')
    try {
      await publishStore.refreshUnpublishedChangesSummary()
    } catch (e) {
      console.warn('⚠️ Failed to refresh unpublished changes summary (non-fatal):', e)
    }
    return
  }

  revertLocalFieldChanges('condition')
  if (shouldClearCertified) {
    revertLocalFieldChanges('certified_by_dealer')
  }
}

const revertOverrideFieldChanges = (field: string) => {
  if (field === 'condition' && isCertifiedDependentOnConditionRelease.value) {
    void revertConditionReleaseWithDependentCertified()
    return
  }

  if (pendingOverrideActions.value[field]?.action === 'REMOVE_OVERRIDE') {
    void revertPendingOverride(field)
    return
  }

  revertLocalFieldChanges(field)
}

/** Discards a single staged (pending) override action via the draft revert endpoint. */
const discardPendingOverrideAction = async (field: string): Promise<boolean> => {
  if (!accountSettings.accountId || !vehicleData.value?.vin) return false
  try {
    await revertManualOverrideDraftAction(
      endpoint,
      accountSettings.accountId,
      String(vehicleData.value.vin),
      field,
      accountSettings.sessionToken,
    )
    const next = { ...pendingOverrideActions.value }
    delete next[field]
    pendingOverrideActions.value = next
    return true
  } catch (e) {
    console.error('Failed to discard pending override:', e)
    makeAlert('Failed to discard pending override', 'error')
    return false
  }
}

const revertPendingOverride = async (field: string) => {
  const discarded = await discardPendingOverrideAction(field)
  if (!discarded) return
  try {
    syncFeedBackedFieldInputs()
    makeAlert('Pending override change discarded', 'success')
    try {
      await publishStore.refreshUnpublishedChangesSummary()
    } catch (e) {
      console.warn('⚠️ Failed to refresh unpublished changes summary (non-fatal):', e)
    }
  } catch (e) {
    console.error('Failed to refresh after discarding pending override:', e)
  }
}
type VehicleFormData = Record<string, unknown> & {
  vin?: string
  vehicle_type?: string
  body_type?: string
  dealer_photos?: unknown
}

const vehicleData = ref<VehicleFormData>({})
// Snapshot of the form data used as baseline for draft diffing (flat keys only).
const draftBaseline = ref<Record<string, unknown> | null>(null)

const vehicleSourceBadgeLabel = computed(() => {
  if (feedType.value === 'auto') return 'Feed-backed'
  if (feedType.value === 'manual') return 'Manual vehicle'
  return null
})

const headerVinLabel = computed(() => {
  const value = String(vehicleData.value?.vin || vin || '').trim().toUpperCase()
  return value ? `VIN: ${value}` : 'VIN: Not set'
})

const headerStockNumberLabel = computed(() => {
  const stock = String((vehicleData.value as Record<string, unknown>)?.stock_number ?? '').trim()
  return stock ? `Stock #${stock}` : 'Stock #: Not set'
})

const headerHelperText = computed(() => {
  if (!vehicleData.value?.vin || isLoadingVehicle.value) return null
  if (feedType.value === 'auto') {
    return 'Feed data is preserved. Changes apply after save and publish.'
  }
  if (feedType.value === 'manual') {
    return 'Changes apply after save and publish.'
  }
  return null
})

const inventoryStateModel = computed<InventoryState | undefined>({
  get: () => (vehicleData.value as Record<string, unknown>).inventoryState as InventoryState | undefined,
  set: (value) => {
    vehicleData.value.inventoryState = value
  },
})
const inventoryStateModeModel = computed<InventoryOwnershipMode | undefined>({
  get: () => readOwnershipMode((vehicleData.value as Record<string, unknown>).inventoryStateMode),
  set: (value) => {
    vehicleData.value.inventoryStateMode = value
  },
})
const inventoryUsageTypeModel = computed<InventoryUsageType | undefined>({
  get: () =>
    (vehicleData.value as Record<string, unknown>).inventoryUsageType as InventoryUsageType | undefined,
  set: (value) => {
    vehicleData.value.inventoryUsageType = value
  },
})
const inventoryUsageTypeModeModel = computed<InventoryOwnershipMode | undefined>({
  get: () => readOwnershipMode((vehicleData.value as Record<string, unknown>).inventoryUsageTypeMode),
  set: (value) => {
    vehicleData.value.inventoryUsageTypeMode = value
  },
})

const currentLifecycleSnapshot = (): InventoryLifecycleSnapshot => ({
  inventoryState: (vehicleData.value as Record<string, unknown>).inventoryState as
    | InventoryState
    | undefined,
  inventoryStateMode: readOwnershipMode(
    (vehicleData.value as Record<string, unknown>).inventoryStateMode,
  ),
  inventoryUsageType: (vehicleData.value as Record<string, unknown>).inventoryUsageType as
    | InventoryUsageType
    | undefined,
  inventoryUsageTypeMode: readOwnershipMode(
    (vehicleData.value as Record<string, unknown>).inventoryUsageTypeMode,
  ),
})

const inventoryStatePendingChangeState = computed(() => {
  const current = currentLifecycleSnapshot()
  const loaded = loadedOperationalBaseline.value
  return current.inventoryState !== loaded.inventoryState ||
    normalizeOwnershipMode(current.inventoryStateMode) !== normalizeOwnershipMode(loaded.inventoryStateMode)
    ? 'unsaved'
    : 'draft'
})

const inventoryUsageTypePendingChangeState = computed(() => {
  const current = currentLifecycleSnapshot()
  const loaded = loadedOperationalBaseline.value
  return current.inventoryUsageType !== loaded.inventoryUsageType ||
    normalizeOwnershipMode(current.inventoryUsageTypeMode) !==
      normalizeOwnershipMode(loaded.inventoryUsageTypeMode)
    ? 'unsaved'
    : 'draft'
})

const currentWebsiteVisibilityBaseline = (): ManualWebsiteVisibilityBaseline =>
  buildManualWebsiteVisibilityBaseline({
    websiteVisibility: websiteVisibilityModel.value,
    selectedReason: selectedWebsiteVisibilityReason.value,
    reasonNotes: websiteVisibilityReasonNotes.value,
  })

const websiteVisibilityFieldErrors = computed(() => {
  if (!isManualDraftVehicle.value) return {}
  return validateManualWebsiteVisibilityForm({
    websiteVisibility: websiteVisibilityModel.value,
    selectedReason: selectedWebsiteVisibilityReason.value,
    reasonNotes: websiteVisibilityReasonNotes.value,
  })
})

const applyManualWebsiteVisibilityState = (baseline: ManualWebsiteVisibilityBaseline) => {
  websiteVisibilityModel.value = normalizeManualWebsiteVisibility(baseline.websiteVisibility)
  const { selectedReason, reasonNotes } = hydrateManualWebsiteVisibilityReason(
    baseline.websiteVisibilityReason,
  )
  selectedWebsiteVisibilityReason.value = selectedReason
  websiteVisibilityReasonNotes.value = reasonNotes
}

const syncLoadedWebsiteVisibilityBaseline = () => {
  loadedWebsiteVisibilityBaseline.value = currentWebsiteVisibilityBaseline()
}

const clearWebsiteVisibilityFieldError = (field: string) => {
  if (!fieldErrors.value[field]) return
  const updated = { ...fieldErrors.value }
  delete updated[field]
  fieldErrors.value = updated
}

const availableFields = ref<string[]>([])
const fieldErrors = ref<Record<string, string>>({})
const originalPhotoUrls = ref<string[]>([])

type NewPhotoItem = {
  kind: 'new'
  id: string
  file: File
  name: string
  ext?: string
  previewUrl: string
}

type CombinedPhotoItem = ExistingPhotoOrderItem | NewPhotoItem
type PhotoSourceMode = 'feed' | 'uploaded'
type UploadedPhotoLibraryImage = {
  key: string
  fileName: string
  publicUrl: string
  lastModified?: string
  size?: number
}

// Note: this ref is inside a v-for (section loop), so Vue will provide an array of elements.
const photoInputRef = ref<HTMLInputElement | HTMLInputElement[] | null>(null)
const photoItems = ref<CombinedPhotoItem[]>([])
const photoSourceMode = ref<PhotoSourceMode>('uploaded')
const uploadedPhotoModeItems = ref<CombinedPhotoItem[]>([])
const uploadedPhotoLibrary = ref<UploadedPhotoLibraryImage[]>([])
const uploadedPhotoLibraryLoading = ref(false)
const uploadedPhotoLibraryError = ref('')

/** Current feed photos shown read-only next to the upload grid (feed-backed vehicles only). */
const feedPhotoUrls = computed<string[]>(() => {
  const base = feedBaseValues.value[DEALER_PHOTOS_FIELD]
  return Array.isArray(base) ? (base as string[]) : []
})

// Feed-backed: mirror the grid into the override's local value so dirty/state detection sees edits.
// New (not yet uploaded) files use a placeholder so they always count as a change.
watch(photoItems, (items) => {
  if (!isFeedBackedVehicle.value) return
  vehicleData.value[DEALER_PHOTOS_FIELD] = items.map((p) =>
    p.kind === 'existing' ? p.url : `new:${p.id}`,
  )
  if (photoSourceMode.value === 'uploaded') {
    uploadedPhotoModeItems.value = [...items]
  }
})
const cameraDialogOpen = ref(false)
const photoPreviewOpen = ref(false)
const photoPreviewIndex = ref(0)

const photoPreviewSlides = computed(() => {
  return photoItems.value
    .map((p) => (p.kind === 'existing' ? p.url : p.previewUrl))
    .filter((s): s is string => Boolean(String(s || '').trim()))
})

const endpoint = String(import.meta.env.VITE_ISE_BACKEND_URL || '').replace(/\/+$/g, '')

const loadUploadedPhotoLibrary = async () => {
  const juid = String(accountSettings.accountId || '').trim()
  const vehicleVin = String(vehicleData.value.vin || vin || '').trim().toUpperCase()
  if (!endpoint || !juid || !vehicleVin) return

  uploadedPhotoLibraryLoading.value = true
  uploadedPhotoLibraryError.value = ''
  try {
    const query = new URLSearchParams({ juid, vin: vehicleVin })
    const headers: Record<string, string> = {}
    if (accountSettings.sessionToken) {
      headers.Authorization = `Bearer ${accountSettings.sessionToken}`
    }
    const response = await fetch(`${endpoint}/upload-vehicle-images?${query.toString()}`, {
      method: 'GET',
      headers,
    })
    if (!response.ok) throw new Error('Unable to load previous uploads')
    const payload = await response.json()
    uploadedPhotoLibrary.value = Array.isArray(payload?.images)
      ? payload.images.filter(
          (image: unknown): image is UploadedPhotoLibraryImage =>
            Boolean(
              image &&
                typeof image === 'object' &&
                typeof (image as UploadedPhotoLibraryImage).key === 'string' &&
                typeof (image as UploadedPhotoLibraryImage).fileName === 'string' &&
                typeof (image as UploadedPhotoLibraryImage).publicUrl === 'string',
            ),
        )
      : []
  } catch (error) {
    console.warn('Failed to load previously uploaded vehicle photos:', error)
    uploadedPhotoLibrary.value = []
    uploadedPhotoLibraryError.value = 'Previous uploads could not be loaded.'
  } finally {
    uploadedPhotoLibraryLoading.value = false
  }
}

const { separateReadyCourtesyVehicles } = usePublishedReadyCourtesySetting()

const conditionOptions = [
  { title: 'New', value: 'new' },
  { title: 'Used', value: 'used' },
]

const inventoryStateOptions = computed(() =>
  buildManualInventoryStateOptions({
    includeReadyCourtesy: separateReadyCourtesyVehicles.value,
    includeArchived: true,
  }),
)

const inventoryUsageTypeOptions: Array<{ title: string; value: InventoryUsageType }> = [
  { title: 'Retail', value: INVENTORY_USAGE_TYPE.RETAIL },
  { title: 'Wholesale', value: INVENTORY_USAGE_TYPE.WHOLESALE },
  { title: 'Demo', value: INVENTORY_USAGE_TYPE.DEMO },
  { title: 'Loaner', value: INVENTORY_USAGE_TYPE.LOANER },
  { title: 'Company vehicle', value: INVENTORY_USAGE_TYPE.COMPANY_VEHICLE },
  { title: 'Service', value: INVENTORY_USAGE_TYPE.SERVICE },
]

const readOwnershipMode = (value: unknown): InventoryOwnershipMode => {
  return normalizeOwnershipMode(value === 'MANUAL' ? 'MANUAL' : 'AUTO')
}

const REQUIRED_FIELDS = ['vin', 'year', 'make', 'model', 'condition', 'price']

const isRequiredField = (field: string): boolean => {
  return REQUIRED_FIELDS.includes(field) && !(isFeedBackedVehicle.value && field === 'price')
}

const additionalConditions = ref<string[]>([])
const additionalConditionsTouched = ref(false)
const hasCpoSignal = ref(false)

const normalizeConditionValue = (c: unknown): 'new' | 'used' | '' => {
  const s = String(c ?? '').trim()
  if (!s) return ''
  const up = s.toUpperCase()
  if (up === 'N' || up === 'NEW') return 'new'
  if (up === 'U' || up === 'USED') return 'used'
  // Legacy/compat: treat CPO as used for manual vehicles.
  if (up === 'C' || up === 'CPO' || s.toLowerCase() === 'cpo') return 'used'
  if (s.toLowerCase() === 'new') return 'new'
  if (s.toLowerCase() === 'used') return 'used'
  return ''
}

const toConditionToken = (c: unknown): 'New' | 'Used' | '' => {
  const v = normalizeConditionValue(c)
  if (v === 'new') return 'New'
  if (v === 'used') return 'Used'
  return ''
}

const isTruthy = (v: unknown): boolean => {
  if (v === true) return true
  if (v === false || v == null) return false
  if (typeof v === 'number') return v !== 0
  const s = String(v).trim().toLowerCase()
  return s === '1' || s === 'true' || s === 'yes' || s === 'y'
}

const isCertifiedChecked = computed(() => {
  const dealer = isTruthy((vehicleData.value as any)?.certified_by_dealer)
  const mfr = isTruthy((vehicleData.value as any)?.certified_by_manufacturer)
  return dealer || mfr || hasCpoSignal.value
})

const isConditionUsed = computed(() => toConditionToken((vehicleData.value as any)?.condition) === 'Used')

/** Feed-backed: always show. Manual: only when condition is Used (existing behavior). */
const showCertifiedCheckbox = computed(() => {
  if (isFeedBackedVehicle.value) return true
  return isConditionUsed.value
})

const isCertifiedCheckboxDisabled = computed(() => {
  if (isFeedBackedVehicle.value) {
    if (!isConditionUsed.value) return true
    return isFeedBackedDisplayFieldDisabled('certified_by_dealer')
  }
  return false
})

const isConditionReleasePendingToNew = computed(() => {
  if (!isFeedBackedVehicle.value) return false
  if (!isFieldReleaseStaged('condition')) return false
  return normalizeConditionValue((vehicleData.value as Record<string, unknown>)?.condition) === 'new'
})

const hasCertifiedReleaseDraft = computed(
  () =>
    pendingOverrideActions.value.certified_by_dealer?.action === 'REMOVE_OVERRIDE' ||
    overrideRemovals.value.has('certified_by_dealer') ||
    isLocalValueAtActiveReleaseTarget('certified_by_dealer'),
)

const isCertifiedDependentOnConditionRelease = computed(
  () =>
    isFeedBackedVehicle.value &&
    isCertifiedCheckboxDisabled.value &&
    isConditionReleasePendingToNew.value &&
    hasCertifiedReleaseDraft.value,
)

const certifiedCheckboxHelperText = computed((): string | undefined => {
  if (isFeedBackedVehicle.value && !isConditionUsed.value) {
    return 'Certification is only available for used vehicles.'
  }
  return undefined
})

const certifiedConditionContext = computed(() => ({
  isFeedBacked: isFeedBackedVehicle.value,
  condition: (vehicleData.value as Record<string, unknown>)?.condition,
  pendingCertifiedAction: pendingOverrideActions.value.certified_by_dealer ?? null,
  certifiedLocallyDirty: isFieldDirty('certified_by_dealer'),
  localCertifiedValue: (vehicleData.value as Record<string, unknown>)?.certified_by_dealer,
}))

const certifiedNewConditionConflictMessage = computed(() =>
  getCertifiedNewConditionConflictMessage(certifiedConditionContext.value),
)

const isCertifiedUi = computed<boolean>({
  get: () => {
    if (isFeedBackedVehicle.value && !isConditionUsed.value) {
      return false
    }
    const dealer = isTruthy((vehicleData.value as any)?.certified_by_dealer)
    const mfr = isTruthy((vehicleData.value as any)?.certified_by_manufacturer)
    if (isFeedBackedVehicle.value) {
      return dealer || mfr
    }
    return (dealer || mfr || hasCpoSignal.value) && isConditionUsed.value
  },
  set: (v) => {
    if (isFeedBackedVehicle.value && !isConditionUsed.value) return
    ;(vehicleData.value as any).certified_by_dealer = v
    if (!v) {
      ;(vehicleData.value as any).certified_by_manufacturer = false
    }
  },
})

watch(
  () => (vehicleData.value as any)?.condition,
  (raw) => {
    const normalized = normalizeConditionValue(raw)
    if (normalized && raw !== normalized) {
      ;(vehicleData.value as any).condition = normalized
    }
    if (!isCertifiedAllowedForCondition(normalized || raw)) {
      ;(vehicleData.value as any).certified_by_dealer = false
      ;(vehicleData.value as any).certified_by_manufacturer = false
      if (overrideSettings.value.certified_by_dealer) {
        const next = { ...overrideSettings.value }
        delete next.certified_by_dealer
        overrideSettings.value = next
      }
    }
  },
  { immediate: true },
)

watch(
  () => (vehicleData.value as any)?.conditions,
  (existing) => {
    const existingArr: string[] = Array.isArray(existing)
      ? (existing as unknown[]).map((x) => String(x)).filter(Boolean)
      : typeof existing === 'string'
        ? existing
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : []

    if (existingArr.some((x) => String(x).trim().toUpperCase() === 'CPO')) {
      hasCpoSignal.value = true
    }

    if (additionalConditionsTouched.value) return

    const base = toConditionToken((vehicleData.value as any)?.condition)
    const extras = existingArr
      .map((x) => String(x).trim())
      .filter((t) => {
        if (!t) return false
        if (base && t.toLowerCase() === base.toLowerCase()) return false
        if (t.toLowerCase() === 'certified') return false
        if (t.toUpperCase() === 'CPO') return false
        return true
      })

    if (!sameStringArray(additionalConditions.value, extras)) {
      additionalConditions.value = extras
    }
  },
  { immediate: true, deep: true },
)

watch(
  [
    () => (vehicleData.value as any)?.condition,
    () => additionalConditions.value,
    () => isCertifiedUi.value,
    () => hasCpoSignal.value,
  ],
  () => {
    const base = toConditionToken((vehicleData.value as any)?.condition)
    const out: string[] = []
    if (base) out.push(base)

    if (base === 'Used' && isCertifiedUi.value) out.push('Certified')

    for (const raw of additionalConditions.value) {
      const t = String(raw ?? '').trim()
      if (!t) continue
      if (base && t.toLowerCase() === base.toLowerCase()) continue
      if (t.toLowerCase() === 'certified') continue
      out.push(t)
    }

    const dedup = Array.from(new Set(out))
    const current = Array.isArray((vehicleData.value as any)?.conditions)
      ? ((vehicleData.value as any).conditions as unknown[]).map((x: unknown) => String(x))
      : []

    if (!sameStringArray(current, dedup)) {
      ;(vehicleData.value as any).conditions = dedup
    }
  },
  { immediate: true, deep: true },
)

const isNonEmpty = (v: unknown): boolean => {
  if (v == null) return false
  if (typeof v === 'string') return v.trim().length > 0
  return true
}

const liveFieldErrors = computed<Record<string, string>>(() => {
  const data = (vehicleData.value || {}) as Record<string, unknown>
  const errs: Record<string, string> = {}
  for (const k of REQUIRED_FIELDS) {
    if (!isRequiredField(k)) continue
    if (!isNonEmpty((data as any)[k])) errs[k] = 'Required'
  }
  return errs
})

// Merge "live" validation with runtime/submit errors (submit errors win)
const uiFieldErrors = computed<Record<string, string>>(() => {
  return { ...liveFieldErrors.value, ...fieldErrors.value }
})

const isFormValidForSave = computed(() => Object.keys(liveFieldErrors.value).length === 0)

const sectionSlug = (label: string): string =>
  label
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

const sectionCardTestId = (label: string): string => `edit-vehicle-section-card-${sectionSlug(label)}`

const sectionTitleTestId = (label: string): string | undefined =>
  label === 'Essential Information' ? 'edit-vehicle-essential-information-heading' : undefined

const sectionHelperText = (label: string): string | undefined => {
  const section = EDIT_VEHICLE_SECTIONS.find((s) => s.label === label)
  return section ? EDIT_VEHICLE_SECTION_HELPERS[section.id] : undefined
}

// Organize fields into visual sections
const fieldSections = computed(() => {
  return EDIT_VEHICLE_SECTIONS.filter(
    (section) =>
      section.id !== 'inventory_lifecycle' &&
      !(isFeedBackedVehicle.value && FEED_BACKED_HIDDEN_SECTION_IDS.has(section.id)),
  ).map(
    (section) => {
      const fields =
        section.id === 'media_photos'
          ? []
          : getEditVehicleDynamicFieldsForSection(section.id, availableFields.value).filter(
              shouldShowField,
            )

      return {
        id: section.id as EditVehicleSectionId,
        label: section.label,
        fields,
      }
    },
  ).filter(
    (section) =>
      section.label === 'Essential Information' ||
      section.id === 'media_photos' ||
      section.fields.length > 0 ||
      (section.id === 'pricing_mileage' && hasIgnoredPricingHubPriceOverride.value),
  )
})

// Conditional field visibility logic
const shouldShowField = (field: string): boolean => {
  if (isFeedBackedVehicle.value && feedBackedHiddenFieldIds.value.has(field)) return false
  if (!hasVehicleHistoryFieldPresence(field)) return false

  if (field === 'cab_style') {
    const vehicleType = String(vehicleData.value.vehicle_type ?? '').toLowerCase()
    const bodyType = String(vehicleData.value.body_type ?? '').toLowerCase()
    return vehicleType.includes('truck') || bodyType.includes('truck')
  }
  return true
}

const clearFieldError = (field: string) => {
  if (fieldErrors.value[field]) {
    const updated = { ...fieldErrors.value }
    delete updated[field]
    fieldErrors.value = updated
  }
}

// Fetch available fields from API
const fetchFields = async () => {
  isLoadingFields.value = true
  try {
    const resp = await fetchIseMappingFields(endpoint, accountSettings.accountId)
    const keys = extractIseMappingFieldKeys(resp)
    // Per-field source provenance (feed-backed override staging). Empty for manual vehicles.
    fieldProvenance.value = extractFieldProvenanceMap(resp)
    availableFields.value = keys.length ? keys : [...(DISPLAY_FIELD_ORDER as unknown as string[])]
  } catch (error) {
    console.error('Error fetching fields:', error)
    makeAlert('Failed to load vehicle fields', 'error')
  } finally {
    isLoadingFields.value = false
  }
}

const applyVehicleDraftOverlay = (draft: VehicleDraftRecord | null | undefined) => {
  if (!draft) {
    draftIntent.value = null
    loadedDraftFieldKeys.value = new Set()
    return
  }

  draftIntent.value = draft.intent ?? null
  loadedDraftFieldKeys.value = new Set(Object.keys(draft.fields || {}))

  if (draft?.fields && typeof draft.fields === 'object') {
    for (const [k, entry] of Object.entries(draft.fields)) {
      ;(vehicleData.value as any)[k] = (entry as any)?.value
    }
  }
  if (draft?.operationalPatch && typeof draft.operationalPatch === 'object') {
    Object.assign(vehicleData.value, draft.operationalPatch)
  }
}

const buildVehicleDataFromDraft = (draft: VehicleDraftRecord): void => {
  const fromDraft: Record<string, unknown> = {}
  for (const [k, entry] of Object.entries(draft.fields || {})) {
    fromDraft[k] = (entry as any)?.value
  }

  feedType.value = 'manual'
  publishState.value = 'draft'
  draftIntent.value = draft.intent ?? 'CREATE'

  vehicleData.value = sanitizeVehicleRecordForDisplay({
    ...fromDraft,
    vin: String(vin),
    inventoryState:
      draft.operationalPatch?.inventoryState ??
      (fromDraft.inventoryState as InventoryState | undefined) ??
      INVENTORY_STATE.UNKNOWN,
    inventoryStateMode: readOwnershipMode(draft.operationalPatch?.inventoryStateMode),
    inventoryUsageType:
      draft.operationalPatch?.inventoryUsageType ??
      (fromDraft.inventoryUsageType as InventoryUsageType | undefined) ??
      INVENTORY_USAGE_TYPE.RETAIL,
    inventoryUsageTypeMode: readOwnershipMode(draft.operationalPatch?.inventoryUsageTypeMode),
  }) as any
  persistedVehicleBaseline.value = JSON.parse(JSON.stringify(vehicleData.value || {}))
  loadedDraftFieldKeys.value = new Set(Object.keys(draft.fields || {}))

  if (draft.operationalPatch && typeof draft.operationalPatch === 'object') {
    Object.assign(vehicleData.value, draft.operationalPatch)
  }

  liveOperationalBaseline.value = {
    inventoryState: (vehicleData.value as any)?.inventoryState,
    inventoryStateMode: readOwnershipMode((vehicleData.value as any)?.inventoryStateMode),
    inventoryUsageType: (vehicleData.value as any)?.inventoryUsageType,
    inventoryUsageTypeMode: readOwnershipMode((vehicleData.value as any)?.inventoryUsageTypeMode),
  }

  applyManualWebsiteVisibilityState(
    readManualWebsiteVisibilityFromSources({
      operationalPatch: draft.operationalPatch,
      vehicle: vehicleData.value as Record<string, unknown>,
    }),
  )
}

const finalizeLoadedVehicleState = async () => {
  const dealerPhotos = Array.isArray(vehicleData.value.dealer_photos)
    ? (vehicleData.value.dealer_photos as string[])
    : []
  originalPhotoUrls.value = [...dealerPhotos]
  photoItems.value = toExistingPhotoItems(dealerPhotos)
  await fetchFields()
  if (isFeedBackedVehicle.value) {
    syncFeedBackedFieldInputs()
  } else {
    syncDraftBaselineFromForm()
  }
  loadedOperationalBaseline.value = currentLifecycleSnapshot()
  syncLoadedWebsiteVisibilityBaseline()
  if (feedType.value === 'manual') {
    void loadChromeEnhancementSource()
  }
  void loadUploadedPhotoLibrary()
}

const tryLoadManualDraftFromVehicleDraft = async (): Promise<boolean> => {
  try {
    const draftResp = await fetchVehicleDraft(
      endpoint,
      accountSettings.accountId,
      vin,
      accountSettings.sessionToken,
    )
    const draft = draftResp?.draft
    if (!draft) return false

    const hasFields = draft.fields && typeof draft.fields === 'object'
    const hasOperationalPatch = draft.operationalPatch && typeof draft.operationalPatch === 'object'
    if (!hasFields && !hasOperationalPatch) return false

    buildVehicleDataFromDraft(draft)
    await finalizeLoadedVehicleState()
    return true
  } catch (e) {
    console.warn('⚠️ Failed to load manual draft from VehicleDraft (non-fatal):', e)
    return false
  }
}

// Load vehicle data
const loadVehicle = async () => {
  // If account context isn't ready yet, keep the loading state until it is.
  if (!vin) {
    isLoadingVehicle.value = false
    return
  }
  if (!accountSettings.accountId) return

  try {
    const resp = await fetchVehicleByVin(endpoint, accountSettings.accountId, vin)
    feedType.value = resp.feedType ?? null
    publishState.value = resp.publishState ?? (resp.feedType === 'manual' ? 'draft' : 'published')

    // Committed ACTIVE overrides (feed-backed only) keyed by field, for display + UPDATE/REMOVE base.
    const activeMap: Record<string, ActiveManualOverride> = {}
    for (const ov of resp.manualOverrides?.active ?? []) {
      if (ov?.field) activeMap[ov.field] = ov
    }
    activeOverrides.value = activeMap
    overrideRemovals.value = new Set()
    overrideSettings.value = {}

    if (!resp.vehicle) {
      const loadedFromDraft = await tryLoadManualDraftFromVehicleDraft()
      if (!loadedFromDraft) {
        makeAlert('Vehicle not found in inventory', 'error')
        router.push('/inventory')
      }
      return
    }

    const liveInventoryState =
      ((resp.vehicle as any)?.inventoryState as InventoryState | undefined) ?? INVENTORY_STATE.UNKNOWN
    const liveInventoryStateMode = readOwnershipMode(
      (resp.vehicle as any)?.inventoryStateMode ?? resp.inventoryStateMode,
    )
    const liveInventoryStateChangedAt =
      (resp.vehicle as any)?.inventoryStateChangedAt ?? resp.inventoryStateChangedAt
    const liveInventoryStateChangedBy =
      (resp.vehicle as any)?.inventoryStateChangedBy ?? resp.inventoryStateChangedBy
    const liveInventoryUsageType =
      ((resp.vehicle as any)?.inventoryUsageType as InventoryUsageType | undefined) ??
      INVENTORY_USAGE_TYPE.RETAIL
    const liveInventoryUsageTypeMode = readOwnershipMode(
      (resp.vehicle as any)?.inventoryUsageTypeMode ?? resp.inventoryUsageTypeMode,
    )
    const liveInventoryUsageTypeChangedAt =
      (resp.vehicle as any)?.inventoryUsageTypeChangedAt ?? resp.inventoryUsageTypeChangedAt
    const liveInventoryUsageTypeChangedBy =
      (resp.vehicle as any)?.inventoryUsageTypeChangedBy ?? resp.inventoryUsageTypeChangedBy

    liveOperationalBaseline.value = {
      inventoryState: liveInventoryState,
      inventoryStateMode: liveInventoryStateMode,
      inventoryUsageType: liveInventoryUsageType,
      inventoryUsageTypeMode: liveInventoryUsageTypeMode,
    }

    // Backend may return SRP data without vin; ensure vin is always set for rendering/saving.
    vehicleData.value = sanitizeVehicleRecordForDisplay({
      ...(resp.vehicle || {}),
      vin: String(resp.vin || vin),
      inventoryState: liveInventoryState,
      inventoryStateMode: liveInventoryStateMode,
      inventoryStateChangedAt: liveInventoryStateChangedAt,
      inventoryStateChangedBy: liveInventoryStateChangedBy,
      inventoryUsageType: liveInventoryUsageType,
      inventoryUsageTypeMode: liveInventoryUsageTypeMode,
      inventoryUsageTypeChangedAt: liveInventoryUsageTypeChangedAt,
      inventoryUsageTypeChangedBy: liveInventoryUsageTypeChangedBy,
    }) as any
    persistedVehicleBaseline.value = JSON.parse(JSON.stringify(vehicleData.value || {}))

    // Overlay any saved draft fields so re-opening Edit reflects draft state (not live state).
    let draftOperationalPatch: Record<string, unknown> | undefined
    try {
      const draftResp = await fetchVehicleDraft(
        endpoint,
        accountSettings.accountId,
        vin,
        accountSettings.sessionToken,
      )
      draftOperationalPatch = draftResp?.draft?.operationalPatch as Record<string, unknown> | undefined
      applyVehicleDraftOverlay(draftResp?.draft)
      pendingOverrideActions.value = draftResp?.draft?.manualOverrideActions ?? {}
    } catch (e) {
      draftIntent.value = null
      pendingOverrideActions.value = {}
      // Non-fatal: editing can proceed from live snapshot.
      console.warn('⚠️ Failed to load vehicle draft (non-fatal):', e)
    }

    if (feedType.value === 'manual' && publishState.value === 'draft') {
      applyManualWebsiteVisibilityState(
        readManualWebsiteVisibilityFromSources({
          operationalPatch: draftOperationalPatch,
          vehicle: vehicleData.value as Record<string, unknown>,
          respWebsiteVisibility: (resp as { websiteVisibility?: unknown }).websiteVisibility,
          respWebsiteVisibilityReason: (resp as { websiteVisibilityReason?: unknown })
            .websiteVisibilityReason,
        }),
      )
    }

    await finalizeLoadedVehicleState()
  } catch (error) {
    console.error('Failed to load vehicle:', error)
    makeAlert('Failed to load vehicle data', 'error')
  } finally {
    isLoadingVehicle.value = false
  }
}

const triggerPhotoSelection = () => {
  const el = Array.isArray(photoInputRef.value) ? photoInputRef.value[0] : photoInputRef.value
  el?.click?.()
}

const setPhotoSourceMode = (mode: PhotoSourceMode) => {
  if (!isFeedBackedVehicle.value || photoSourceMode.value === mode) return

  if (mode === 'feed') {
    uploadedPhotoModeItems.value = [...photoItems.value]
    photoSourceMode.value = 'feed'
    photoItems.value = []
    return
  }

  photoSourceMode.value = 'uploaded'
  photoItems.value = [...uploadedPhotoModeItems.value]
}

const isUploadedLibraryImageSelected = (url: string): boolean => {
  const normalized = String(url || '').trim().toLowerCase()
  return photoItems.value.some(
    (item) => item.kind === 'existing' && item.url.trim().toLowerCase() === normalized,
  )
}

const toggleUploadedLibraryImage = (image: UploadedPhotoLibraryImage) => {
  if (photoSourceMode.value !== 'uploaded') setPhotoSourceMode('uploaded')
  const normalized = image.publicUrl.trim().toLowerCase()
  const selected = photoItems.value.find(
    (item) => item.kind === 'existing' && item.url.trim().toLowerCase() === normalized,
  )
  if (selected) {
    removePhotoItem(selected.id)
    return
  }
  photoItems.value = [
    ...photoItems.value,
    {
      kind: 'existing',
      id: `library_${image.key}_${Date.now()}`,
      url: image.publicUrl,
    },
  ]
}

const openCameraDialog = () => {
  cameraDialogOpen.value = true
}

const addFilesToPhotoItems = (files: File[]) => {
  if (files.length === 0) return
  if (isFeedBackedVehicle.value && photoSourceMode.value !== 'uploaded') {
    setPhotoSourceMode('uploaded')
  }

  const next: NewPhotoItem[] = files.map((file) => {
    const m = file.name.match(/\.([^.]+)$/)
    const ext = m?.[1]?.toLowerCase()
    return {
      kind: 'new',
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      file,
      name: file.name.replace(/\.[^.]+$/, ''),
      ext,
      previewUrl: URL.createObjectURL(file),
    }
  })

  photoItems.value = [...photoItems.value, ...next]
}

const handlePhotoSelection = (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = input.files ? Array.from(input.files) : []
  addFilesToPhotoItems(files)
  // reset input to allow re-selecting same file
  input.value = ''
}

const handleCameraCapture = (files: File[]) => {
  addFilesToPhotoItems(files)
}

const removePhotoItem = (id: string) => {
  const item = photoItems.value.find((p) => p.id === id)
  if (item && item.kind === 'new' && item.previewUrl) {
    URL.revokeObjectURL(item.previewUrl)
  }
  photoItems.value = photoItems.value.filter((p) => p.id !== id)
}

const clearNewPhotoPreviews = () => {
  photoItems.value.forEach((p) => {
    if (p.kind === 'new' && p.previewUrl) URL.revokeObjectURL(p.previewUrl)
  })
}

const openPhotoPreview = (src: string) => {
  const next = String(src || '').trim()
  if (!next) return
  const idx = photoPreviewSlides.value.indexOf(next)
  photoPreviewIndex.value = idx >= 0 ? idx : 0
  photoPreviewOpen.value = true
}

watch(photoPreviewOpen, (open) => {
  if (!open) photoPreviewIndex.value = 0
})

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const val = bytes / Math.pow(k, i)
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${sizes[i]}`
}

function sameStringArray(a: string[], b: string[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

const EDIT_VEHICLE_FIELD_LABELS: Record<string, string> = {
  date_in_stock_ts: 'Date In Stock',
  has_carfax: 'Has CARFAX',
  carfax_one_owner: 'CARFAX One Owner',
  auto_check: 'AutoCheck',
  ...EDIT_VEHICLE_SPECS_EFFICIENCY_FIELD_LABELS,
}

const EDIT_VEHICLE_FIELD_HINTS: Record<string, string> = {
  price: 'Initial/source price',
  display_price: 'Final website price',
}

const getFieldHint = (field: string): string | undefined => EDIT_VEHICLE_FIELD_HINTS[field]

// Format field label
const formatFieldLabel = (field: string): string => {
  if (EDIT_VEHICLE_FIELD_LABELS[field]) {
    const label = EDIT_VEHICLE_FIELD_LABELS[field]
    return isRequiredField(field) ? `${label} *` : label
  }

  const label = field
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

  return isRequiredField(field) ? `${label} *` : label
}

// Go back
const fromRoute = computed(() => String(route.query?.from || ''))

const detailsRouteForVin = () => ({
  path: `/inventory/${vin}`,
  query: fromRoute.value ? { from: fromRoute.value } : undefined,
})

const goBack = () => {
  router.push(detailsRouteForVin())
}

async function removeDraftFields(fields: Iterable<string>) {
  const uniqueFields = Array.from(new Set(Array.from(fields).filter(Boolean)))
  if (uniqueFields.length === 0 || !accountSettings.accountId) return

  await Promise.all(
    uniqueFields.map((field) =>
      revertVehicleDraftField(
        endpoint,
        accountSettings.accountId,
        String(vehicleData.value.vin || vin),
        field,
        accountSettings.sessionToken,
      ),
    ),
  )

  loadedDraftFieldKeys.value = new Set(
    Array.from(loadedDraftFieldKeys.value).filter((field) => !uniqueFields.includes(field)),
  )
}

async function removeManualOverrideDraftActions(fields: Iterable<string>) {
  const uniqueFields = Array.from(new Set(Array.from(fields).filter(Boolean)))
  if (uniqueFields.length === 0 || !accountSettings.accountId) return

  await Promise.all(
    uniqueFields.map((field) =>
      revertManualOverrideDraftAction(
        endpoint,
        accountSettings.accountId,
        String(vehicleData.value.vin || vin),
        field,
        accountSettings.sessionToken,
      ),
    ),
  )

  const nextPending = { ...pendingOverrideActions.value }
  for (const field of uniqueFields) {
    delete nextPending[field]
  }
  pendingOverrideActions.value = nextPending
}

// Validate required fields
const validateForm = (): boolean => {
  const errors: Record<string, string> = {}
  
  for (const field of REQUIRED_FIELDS) {
    if (!isRequiredField(field)) continue
    if (!isNonEmpty((vehicleData.value as any)[field])) {
      errors[field] = `${formatFieldLabel(field)} is required`
    }
  }

  const dateInStockInput = hasDraftFieldInputValue('date_in_stock_ts')
    ? draftFieldValues.value.date_in_stock_ts
    : getDraftFieldInputValue('date_in_stock_ts')
  const dateInStockError = getDraftFieldValidationError('date_in_stock_ts', dateInStockInput)
  if (dateInStockError) {
    errors.date_in_stock_ts = dateInStockError
  }

  if (isManualDraftVehicle.value) {
    Object.assign(errors, websiteVisibilityFieldErrors.value)
  }
  
  fieldErrors.value = errors
  return Object.keys(errors).length === 0
}

// Save vehicle changes
const saveVehicle = async () => {
  commitAllDraftFieldInputValues()

  // Validate form
  if (!validateForm()) {
    makeAlert('Please fill in all required fields', 'error')
    return
  }

  if (!accountSettings.accountId) {
    makeAlert('Account ID not found', 'error')
    return
  }

  isSaving.value = true

  try {
    // Upload new photos if any, then update dealer_photos in the same update call.
    const newItems = photoItems.value.filter((p) => p.kind === 'new') as NewPhotoItem[]
    const uploadedById: Record<string, string> = {}
    if (newItems.length > 0) {
      const timestampBase = Date.now()
      const sanitizedVin = String(vehicleData.value.vin || '').trim().toUpperCase()
      const generatedFileNames = newItems.map((photo, index) => {
        const providedBase = (photo.name || '').trim()
        const base = providedBase.length > 0 ? providedBase : `${sanitizedVin}_${timestampBase}_${index + 1}`
        const ext = (photo.ext || photo.file.name.match(/\.([^.]+)$/)?.[1] || 'jpg').toLowerCase()
        return `${base}.${ext}`
      })

      const presignedResponse = await fetch(`${endpoint}/upload-vehicle-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accountSettings.sessionToken
            ? { Authorization: `Bearer ${accountSettings.sessionToken}` }
            : {}),
        },
        body: JSON.stringify({
          juid: accountSettings.accountId,
          vin: sanitizedVin,
          imageCount: newItems.length,
          fileNames: generatedFileNames,
        }),
      })
      if (!presignedResponse.ok) throw new Error('Failed to request upload URLs')
      const presignedData = await presignedResponse.json()
      const urls = Array.isArray(presignedData?.urls) ? presignedData.urls : []
      if (urls.length !== newItems.length) throw new Error('Upload URL count mismatch')

      for (let i = 0; i < newItems.length; i++) {
        const photo = newItems[i]
        const uploadInfo = urls[i] || {}
        const uploadUrl: string | undefined = uploadInfo.uploadUrl
        const publicUrl: string | undefined = uploadInfo.publicUrl
        if (!uploadUrl) throw new Error(`Missing upload URL for ${generatedFileNames[i]}`)

        const uploadResponse = await fetch(uploadUrl, { method: 'PUT', body: photo.file })
        if (!uploadResponse.ok) throw new Error(`Failed to upload ${generatedFileNames[i]}`)

        if (publicUrl) uploadedById[photo.id] = publicUrl
      }
    }

    // Prepare payload for custom-feed API
    // Only send valid SRP input fields; strip derived/UI-only fields (e.g. segments_sidebar).
    const cleanedVehicle: Record<string, unknown> = { ...vehicleData.value }
    delete cleanedVehicle.dealer_photos
    delete cleanedVehicle.jazelId
    delete cleanedVehicle.juid

    const updateFields = buildCustomFeedUpdateFields({
      rawVehicle: cleanedVehicle,
      validFieldKeys: availableFields.value,
    })

    const orderItems: PhotoOrderItem[] = photoItems.value.map((p) =>
      p.kind === 'existing'
        ? ({ kind: 'existing', id: p.id, url: p.url } as const)
        : ({ kind: 'new', id: p.id } as const),
    )
    const shouldUpdatePhotos =
      newItems.length > 0 || hasExistingPhotosChanged(originalPhotoUrls.value, orderItems)
    const finalPhotos = shouldUpdatePhotos ? buildFinalPhotoUrls(orderItems, uploadedById) : []

    // Option 2 (draft-only on edit): write changes to VehicleDraft, do NOT update live /custom-feed.
    const baseline = (draftBaseline.value || {}) as Record<string, unknown>
    const overrideBaseline = isFeedBackedVehicle.value
      ? { ...baseline, ...feedBaseValues.value }
      : baseline
    const rawPatch: Record<string, unknown> = {}

    for (const [k, v] of Object.entries(updateFields)) {
      const before = (baseline as any)[k]
      const changed =
        !areDraftFieldValuesEquivalent(k, before, v) ||
        (isFeedBackedVehicle.value &&
          Boolean(pendingOverrideActions.value[k]) &&
          JSON.stringify(before) !== JSON.stringify(v))
      if (changed) rawPatch[k] = v
    }

    if (shouldUpdatePhotos) {
      const beforePhotos = (baseline as any)?.dealer_photos
      const changedPhotos = JSON.stringify(beforePhotos) !== JSON.stringify(finalPhotos)
      if (changedPhotos) rawPatch.dealer_photos = finalPhotos
    }

    let patch: Record<string, unknown> = rawPatch
    let prunedManualDraftFields: string[] = []
    if (!isFeedBackedVehicle.value && !(feedType.value === 'manual' && publishState.value === 'draft')) {
      const pruned = prunePatchNoOps({
        patch: rawPatch,
        persistedValues: persistedVehicleBaseline.value,
      })
      patch = pruned.patch
      prunedManualDraftFields = pruned.prunedFields.filter((field) =>
        loadedDraftFieldKeys.value.has(field),
      )
    }

    // A6: inventoryState / inventoryUsageType are durable operational ownership attributes.
    // Feed-backed vehicles stage value/mode decisions through operationalPatch, never through
    // manualOverrideActions or release-policy controls.
    const currentLifecycle: InventoryLifecycleSnapshot = {
      inventoryState: (vehicleData.value as any)?.inventoryState,
      inventoryStateMode: readOwnershipMode((vehicleData.value as any)?.inventoryStateMode),
      inventoryUsageType: (vehicleData.value as any)?.inventoryUsageType,
      inventoryUsageTypeMode: readOwnershipMode((vehicleData.value as any)?.inventoryUsageTypeMode),
    }
    const baselineLifecycle: InventoryLifecycleSnapshot = isFeedBackedVehicle.value
      ? liveOperationalBaseline.value
      : {
          inventoryState: (baseline as any)?.inventoryState,
          inventoryStateMode: readOwnershipMode((baseline as any)?.inventoryStateMode),
          inventoryUsageType: (baseline as any)?.inventoryUsageType,
          inventoryUsageTypeMode: readOwnershipMode((baseline as any)?.inventoryUsageTypeMode),
        }
    const operationalPatch = buildInventoryLifecycleOperationalPatch({
      current: currentLifecycle,
      baseline: baselineLifecycle,
      feedBacked: isFeedBackedVehicle.value,
    })
    const manualWebsiteVisibilityBaseline = currentWebsiteVisibilityBaseline()
    const hasWebsiteVisibilityChange =
      isManualDraftVehicle.value &&
      hasManualWebsiteVisibilityDiff(
        manualWebsiteVisibilityBaseline,
        loadedWebsiteVisibilityBaseline.value,
      )
    const manualWebsiteVisibilityPayload = isManualDraftVehicle.value
      ? {
          websiteVisibility: manualWebsiteVisibilityBaseline.websiteVisibility,
          websiteVisibilityReason: manualWebsiteVisibilityBaseline.websiteVisibilityReason,
        }
      : {}
    const operationalPatchForDraft = {
      ...operationalPatch,
      ...(hasWebsiteVisibilityChange ? manualWebsiteVisibilityPayload : {}),
    }
    const hasOperationalChange = Object.keys(operationalPatchForDraft).length > 0
    const locallyReleasedOverrideFields = isFeedBackedVehicle.value
      ? collectLocallyReleasedOverrideFields(Object.keys(activeOverrides.value))
      : []
    const token = accountSettings.sessionToken
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.authorization = `Bearer ${token}`

    if (!isFeedBackedVehicle.value && prunedManualDraftFields.length > 0) {
      await removeDraftFields(prunedManualDraftFields)
    }

    if (
      !isFeedBackedVehicle.value &&
      !isVehicleDraftMeaningful({
        patch,
        operationalPatch: hasOperationalChange ? operationalPatchForDraft : undefined,
      })
    ) {
      if (prunedManualDraftFields.length > 0) {
        try {
          await publishStore.refreshUnpublishedChangesSummary()
        } catch (e) {
          console.warn('⚠️ Failed to refresh draft summary after no-op cleanup (non-fatal):', e)
        }
      }
      makeAlert('No changes to save', 'info')
      return
    }

    // If the vehicle itself is still a draft (new manual add not yet published),
    // do NOT use VehicleDraft/VehicleDraftHistory (draft-of-a-draft). Instead, update the draft vehicle directly.
    if (feedType.value === 'manual' && publishState.value === 'draft') {
      const required = {
        year: (vehicleData.value as any)?.year,
        make: (vehicleData.value as any)?.make,
        model: (vehicleData.value as any)?.model,
      }
      const payload: Record<string, unknown> = {
        ...updateFields,
        ...required,
        vin: vehicleData.value.vin,
        jazelId: accountSettings.accountId,
        // A6 operational attributes for a still-draft manual vehicle. The backend routes these to
        // CurrentInventory operationally (and projects inventoryState -> SRP.state); they are not
        // written into the custom-feed display fields.
        ...operationalPatch,
        ...manualWebsiteVisibilityPayload,
      }
      // Draft-first manual creates live in VehicleDraft only until publish; keep updates staged there.
      if (draftIntent.value === 'CREATE') {
        payload.createOnly = true
      }
      if (shouldUpdatePhotos) payload.dealer_photos = finalPhotos

      const resp = await fetch(`${endpoint}/custom-feed`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      })

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to update draft vehicle')
      }

      makeAlert('Draft changes saved. Publish changes when ready.', 'success')

      // Refresh baseline so repeated saves only send new diffs.
      draftBaseline.value = JSON.parse(JSON.stringify(vehicleData.value || {}))
      syncLoadedWebsiteVisibilityBaseline()

      try {
        await publishStore.refreshUnpublishedChangesSummary()
      } catch (e) {
        console.warn('⚠️ Failed to refresh draft vehicle count after draft save (non-fatal):', e)
      }

      if (shouldUpdatePhotos) {
        clearNewPhotoPreviews()
        originalPhotoUrls.value = [...finalPhotos]
        photoItems.value = toExistingPhotoItems(finalPhotos)
      }

      setTimeout(() => {
        router.push(detailsRouteForVin())
      }, 500)

      return
    }

    // Feed-backed vehicles: display-field edits are staged as manual override actions on the
    // VehicleDraft (committed to VehicleManualOverride at publish), never as a custom_feed/patch
    // write — the vehicle stays feed-backed. Operational fields keep the operationalPatch path.
    if (isFeedBackedVehicle.value) {
      const dirtyFields: Record<string, unknown> = {}
      const staleManualOverrideActionFields: string[] = []
      const releaseOverrideFields = new Set([
        ...overrideRemovals.value,
        ...locallyReleasedOverrideFields,
      ])
      for (const [k, v] of Object.entries(patch)) {
        // Photos are staged from the photo grid below (they never flow through the generic diff).
        if (k === DEALER_PHOTOS_FIELD) continue
        if (
          shouldExcludeCertifiedOverrideDirty({
            isFeedBacked: true,
            field: k,
            condition: (vehicleData.value as Record<string, unknown>)?.condition,
          })
        ) {
          continue
        }
        // Missing-provenance fields are disabled in the UI and cannot ADD/UPDATE overrides. If they
        // somehow differ from the baseline (stale local state, browser restore, or mapping drift),
        // ignore the value diff so it does not block unrelated eligible saves.
        if (!isOverrideEligible(k)) continue
        const pendingAction = pendingOverrideActions.value[k]
        if (
          pendingAction &&
          pendingAction.action !== 'REMOVE_OVERRIDE' &&
          !overrideSettings.value[k] &&
          ((activeOverrides.value[k] &&
            areDraftFieldValuesEquivalent(k, v, activeOverrides.value[k].manualValue)) ||
            (!activeOverrides.value[k] &&
              areDraftFieldValuesEquivalent(k, v, feedBaseValues.value[k])))
        ) {
          staleManualOverrideActionFields.push(k)
          continue
        }
        if (
          pendingAction &&
          !overrideSettings.value[k] &&
          k in loadedEffectiveValues.value &&
          areDraftFieldValuesEquivalent(k, v, loadedEffectiveValues.value[k])
        ) {
          continue
        }
        const intent = resolveFeedBackedFieldSaveIntent({
          field: k,
          submittedValue: v,
          currentBaseValue: feedBaseValues.value[k],
          activeOverride: activeOverrides.value[k],
          pendingAction: pendingOverrideActions.value[k],
          hasPolicyChange: Boolean(overrideSettings.value[k]),
        })
        if (intent === 'release') {
          releaseOverrideFields.add(k)
        } else if (intent === 'noop') {
          if (pendingOverrideActions.value[k]) staleManualOverrideActionFields.push(k)
        } else {
          dirtyFields[k] = v
        }
      }

      // Photos: the grid (existing + freshly uploaded URLs) decides ADD/UPDATE, release or discard.
      // Empty never produces an override: an active one is released, a pending one is discarded.
      const photoIntent = resolveFeedBackedPhotoSaveIntent({
        urls: buildFinalPhotoUrls(orderItems, uploadedById),
        activeOverride: activeOverrides.value[DEALER_PHOTOS_FIELD],
        pendingAction: pendingOverrideActions.value[DEALER_PHOTOS_FIELD],
        releaseRequested: releaseOverrideFields.has(DEALER_PHOTOS_FIELD),
        hasPolicyChange: Boolean(overrideSettings.value[DEALER_PHOTOS_FIELD]),
      })
      if (photoIntent.kind === 'stage') {
        releaseOverrideFields.delete(DEALER_PHOTOS_FIELD)
        dirtyFields[DEALER_PHOTOS_FIELD] = photoIntent.manualValue
      } else if (photoIntent.kind === 'release') {
        releaseOverrideFields.add(DEALER_PHOTOS_FIELD)
      } else if (photoIntent.kind === 'discard') {
        staleManualOverrideActionFields.push(DEALER_PHOTOS_FIELD)
      }

      const removedOverrideFields = Array.from(releaseOverrideFields)
      for (const field of removedOverrideFields) {
        delete dirtyFields[field]
      }

      // Per-field policy/expiry change with no local value edit: keep the staged/active manualValue.
      for (const field of Object.keys(overrideSettings.value)) {
        // Photo policy-only changes are already covered by the photo intent (hasPolicyChange).
        if (field === DEALER_PHOTOS_FIELD) continue
        if (field in dirtyFields || removedOverrideFields.includes(field)) continue
        if (
          field === 'certified_by_dealer' &&
          !isCertifiedAllowedForCondition((vehicleData.value as Record<string, unknown>)?.condition)
        ) {
          continue
        }
        const pending = pendingOverrideActions.value[field]
        const hasPendingUpdate = pending && pending.action !== 'REMOVE_OVERRIDE'
        if (!activeOverrides.value[field] && !hasPendingUpdate) continue
        if (!isOverrideEligible(field)) continue
        dirtyFields[field] = resolveEffectiveManualValue(field)
      }

      if (staleManualOverrideActionFields.length > 0) {
        await removeManualOverrideDraftActions(staleManualOverrideActionFields)
      }

      const certifiedSaveError = validateCertifiedConditionOnSave({
        ...certifiedConditionContext.value,
        dirtyFields,
      })
      if (certifiedSaveError) {
        makeAlert(certifiedSaveError, 'error')
        return
      }

      // Per-field explicit settings: convert local datetime-local expiresAt to ISO for the builder.
      const fieldSettings: Record<string, FieldOverrideSetting> = {}
      for (const [f, s] of Object.entries(overrideSettings.value)) {
        fieldSettings[f] = { releasePolicy: s.releasePolicy, expiresAt: toIsoDateTime(s.expiresAt) }
      }

      const { actions, blockedFields, missingExpiresAtFields } = buildManualOverrideActions({
        dirtyFields,
        baseline: overrideBaseline,
        provenance: fieldProvenance.value,
        activeOverrides: activeOverrides.value,
        pendingActions: pendingOverrideActions.value,
        fieldSettings,
        defaultReleasePolicy: DEFAULT_OVERRIDE_RELEASE_POLICY,
        removedOverrideFields,
      })

      if (blockedFields.length > 0) {
        makeAlert(
          `Can't override ${blockedFields.join(', ')}: no resolvable feed source mapping. ` +
            `Remove these edits or fix the dealer mapping configuration before saving.`,
          'error',
        )
        return
      }
      if (missingExpiresAtFields.length > 0) {
        makeAlert(
          `Pick an expiration date/time for the "Until a specific date" policy on: ${missingExpiresAtFields.join(', ')}.`,
          'error',
        )
        return
      }

      const hasActions = Object.keys(actions).length > 0
      if (
        !isVehicleDraftMeaningful({
          manualOverrideActions: actions,
          operationalPatch: hasOperationalChange ? operationalPatch : undefined,
        })
      ) {
        if (staleManualOverrideActionFields.length > 0) {
          try {
            await publishStore.refreshUnpublishedChangesSummary()
          } catch (e) {
            console.warn('⚠️ Failed to refresh draft summary after no-op cleanup (non-fatal):', e)
          }
          syncFeedBackedFieldInputs()
        }
        makeAlert('No changes to save', 'info')
        return
      }

      if (hasActions || hasOperationalChange) {
        const response = await fetch(`${endpoint}/vehicle-drafts`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            jazelId: accountSettings.accountId,
            vin: vehicleData.value.vin,
            ...(hasActions ? { manualOverrideActions: actions } : {}),
            ...(hasOperationalChange ? { operationalPatch } : {}),
          }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to stage manual override changes')
        }
      }

      makeAlert('Vehicle draft saved successfully', 'success')

      if (hasActions) {
        pendingOverrideActions.value = {
          ...pendingOverrideActions.value,
          ...actions,
        }
      }
      loadedOperationalBaseline.value = currentLifecycleSnapshot()
      syncFeedBackedFieldInputs()
      overrideRemovals.value = new Set()
      overrideSettings.value = {}

      try {
        await publishStore.refreshUnpublishedChangesSummary()
      } catch (e) {
        console.warn('⚠️ Failed to refresh unpublished changes summary (non-fatal):', e)
      }

      setTimeout(() => {
        router.push(detailsRouteForVin())
      }, 500)

      return
    }

    // Manual (published) vehicles: write display/SRP field changes and/or operational changes
    // (inventoryState / inventoryUsageType) to the VehicleDraft.
    if (Object.keys(patch).length > 0 || hasOperationalChange) {
      const response = await fetch(`${endpoint}/vehicle-drafts`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          jazelId: accountSettings.accountId,
          vin: vehicleData.value.vin,
          patch,
          ...(hasOperationalChange ? { operationalPatch } : {}),
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to save vehicle draft')
      }

      const result = await response.json()
      console.log('✅ Vehicle draft saved successfully:', result)
    }

    makeAlert('Vehicle draft saved successfully', 'success')

    // Refresh baseline so repeated saves only send new diffs.
    const savedVehicleState = shouldUpdatePhotos
      ? applySavedPhotoUrlsToVehicle(vehicleData.value as Record<string, unknown>, finalPhotos)
      : (vehicleData.value as Record<string, unknown>)
    draftBaseline.value = JSON.parse(JSON.stringify(savedVehicleState))
    loadedOperationalBaseline.value = currentLifecycleSnapshot()

    // Ensure the left-nav "Unpublished Changes" link appears immediately for draft saves.
    try {
      await publishStore.refreshUnpublishedChangesSummary()
    } catch (e) {
      // Non-fatal; nav will update on refresh/next poll.
      console.warn('⚠️ Failed to refresh draft vehicle count after edit save (non-fatal):', e)
    }

    // Update local photo state after successful save
    if (shouldUpdatePhotos) {
      vehicleData.value.dealer_photos = [...finalPhotos]
      clearNewPhotoPreviews()
      originalPhotoUrls.value = [...finalPhotos]
      photoItems.value = toExistingPhotoItems(finalPhotos)
    }

    // Navigate back to details page after a short delay
    setTimeout(() => {
      router.push(detailsRouteForVin())
    }, 1000)
  } catch (error) {
    console.error('Failed to save vehicle:', error)
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    makeAlert(`Failed to update vehicle: ${errorMessage}`, 'error')
  } finally {
    isSaving.value = false
  }
}

const chromeEnhancementSourcePhase = ref<EnhancementSourceLoadStatus>('idle')
const chromeSourceValues = ref<Record<string, unknown> | null>(null)
const enhancementSourceNotices = ref<Record<string, string>>({})
const enhanceDataDialogOpen = ref(false)
const enhanceDataDialogResetToken = ref(0)
let chromeSourceGeneration = 0
let chromeSourceFlight: Promise<void> | null = null
let chromeSourceBoundIdentity = ''

const chromeSourceNotApplicableMessage = `${CHROME_ENHANCEMENT_SOURCE.label} did not provide an applicable value.`
const chromeEnhancementUnavailableMessage = `${CHROME_ENHANCEMENT_SOURCE.label} could not be loaded.`

type EnhancementFieldApplyResult = 'applied' | 'not_applicable' | 'rejected'

function chromeEnhancementIdentity(): string {
  const accountId = String(accountSettings.accountId || '').trim()
  const currentVin = String(vehicleData.value?.vin || route.params.vin || '').trim()
  return `${accountId}\u0000${currentVin}`
}

function discardChromeEnhancementSource() {
  chromeSourceGeneration += 1
  chromeSourceFlight = null
  chromeSourceBoundIdentity = ''
  chromeEnhancementSourcePhase.value = 'idle'
  chromeSourceValues.value = null
  enhancementSourceNotices.value = {}
}

function bindChromeEnhancementIdentity(): string {
  const identity = chromeEnhancementIdentity()
  if (chromeSourceBoundIdentity && chromeSourceBoundIdentity !== identity) {
    discardChromeEnhancementSource()
  }
  chromeSourceBoundIdentity = identity
  return identity
}

function setEnhancementSourceNotice(field: string, message: string | null) {
  const next = { ...enhancementSourceNotices.value }
  if (message) next[field] = message
  else delete next[field]
  enhancementSourceNotices.value = next
}

function preparedChromeEnhancementCandidate(field: string) {
  if (feedType.value !== 'manual' || chromeEnhancementSourcePhase.value !== 'loaded') return null
  return prepareEditVehicleEnhancementCandidate({
    field,
    sanitizedValues: chromeSourceValues.value,
    dealerTimeZone: dealerTimeZone.value,
  })
}

function isChromeEnhancementCandidateApplicable(field: string): boolean {
  return preparedChromeEnhancementCandidate(field)?.applicable === true
}

function chromeEnhancementCandidateDisplay(field: string): string {
  const prepared = preparedChromeEnhancementCandidate(field)
  if (!prepared?.applicable) return ''
  return formatPreparedEnhancementValue(prepared.preparedValue)
}

async function loadChromeEnhancementSource(): Promise<void> {
  const identity = bindChromeEnhancementIdentity()
  if (chromeEnhancementSourcePhase.value === 'loaded') return
  if (chromeEnhancementSourcePhase.value === 'error') return
  if (chromeSourceFlight) return chromeSourceFlight

  const generation = chromeSourceGeneration
  let finishFlight: () => void = () => {}
  const flight = new Promise<void>((resolve) => {
    finishFlight = resolve
  })
  chromeSourceFlight = flight
  chromeEnhancementSourcePhase.value = 'loading'

  void (async () => {
    try {
      const response = await fetch(`${endpoint}/custom-feed-prefill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          juid: accountSettings.accountId,
          vin: String(vehicleData.value?.vin || route.params.vin || '').trim(),
          enhancementId: CHROME_ENHANCEMENT_SOURCE.id,
        }),
      })
      if (generation !== chromeSourceGeneration || chromeSourceBoundIdentity !== identity) return
      if (!response.ok) throw new Error(`Chrome prefill failed (${response.status})`)
      const payload: unknown = await response.json()
      if (generation !== chromeSourceGeneration || chromeSourceBoundIdentity !== identity) return
      chromeSourceValues.value = sanitizeAddVehicleEnhancementPrefill(payload).values
      chromeEnhancementSourcePhase.value = 'loaded'
    } catch {
      if (generation !== chromeSourceGeneration || chromeSourceBoundIdentity !== identity) return
      chromeSourceValues.value = null
      chromeEnhancementSourcePhase.value = 'error'
    } finally {
      if (chromeSourceFlight === flight) chromeSourceFlight = null
      finishFlight()
    }
  })()

  return flight
}

function retryChromeEnhancementSource(): Promise<void> {
  if (chromeSourceFlight) return chromeSourceFlight
  chromeEnhancementSourcePhase.value = 'idle'
  chromeSourceValues.value = null
  return loadChromeEnhancementSource()
}

function restoreRejectedEnhancementDraft(params: {
  field: string
  previousValue: unknown
  hadDraft: boolean
  previousDraft: unknown
  previousError: string | undefined
}) {
  const vehicle = vehicleData.value as Record<string, unknown>
  vehicle[params.field] = params.previousValue
  if (params.hadDraft) {
    draftFieldValues.value = {
      ...draftFieldValues.value,
      [params.field]: params.previousDraft,
    }
  } else {
    const nextDraft = { ...draftFieldValues.value }
    delete nextDraft[params.field]
    draftFieldValues.value = nextDraft
  }
  if (params.previousError) {
    fieldErrors.value = {
      ...fieldErrors.value,
      [params.field]: params.previousError,
    }
  } else {
    clearFieldError(params.field)
  }
}

function applyEnhancementCandidateToField(field: string): EnhancementFieldApplyResult {
  if (feedType.value !== 'manual' || chromeEnhancementSourcePhase.value !== 'loaded') {
    return 'not_applicable'
  }
  const prepared = preparedChromeEnhancementCandidate(field)
  if (!prepared?.applicable) return 'not_applicable'

  if (prepared.inputType === 'boolean') {
    setBooleanFieldInputValue(field, prepared.preparedValue)
    return 'applied'
  }

  if (prepared.inputType === 'select') {
    ;(vehicleData.value as Record<string, unknown>)[field] = prepared.preparedValue
    clearFieldError(field)
    return 'applied'
  }

  const vehicle = vehicleData.value as Record<string, unknown>
  const previousValue = vehicle[field]
  const hadDraft = hasDraftFieldInputValue(field)
  const previousDraft = hadDraft ? draftFieldValues.value[field] : undefined
  const previousError = fieldErrors.value[field]

  setDraftFieldInputValue(field, prepared.preparedValue)
  const draftWritten =
    hasDraftFieldInputValue(field) &&
    Object.is(draftFieldValues.value[field], prepared.preparedValue)
  if (!draftWritten) return 'rejected'

  commitDraftFieldInputValue(field)
  if (!hasDraftFieldInputValue(field)) return 'applied'

  restoreRejectedEnhancementDraft({
    field,
    previousValue,
    hadDraft,
    previousDraft,
    previousError,
  })
  return 'rejected'
}

function applyChromeEnhancementCandidate(field: string) {
  if (feedType.value !== 'manual' || chromeEnhancementSourcePhase.value !== 'loaded') return
  const result = applyEnhancementCandidateToField(field)
  if (result === 'applied') {
    setEnhancementSourceNotice(field, null)
    return
  }
  setEnhancementSourceNotice(field, chromeSourceNotApplicableMessage)
}

function resetEnhanceVehicleDataDialog() {
  enhanceDataDialogOpen.value = false
  enhanceDataDialogResetToken.value += 1
}

function readEnhancementControlValue(field: string): unknown {
  const inputType = getFieldInputType(field)
  if (inputType === 'boolean') return getBooleanFieldInputValue(field)
  if (inputType === 'select') return (vehicleData.value as Record<string, unknown>)[field]
  return getDraftFieldInputValue(field)
}

function collectVisibleEnhancementFieldSnapshots(): BulkEnhancementFieldSnapshot[] {
  const snapshots: BulkEnhancementFieldSnapshot[] = []
  for (const section of fieldSections.value) {
    if (section.id === 'media_photos') continue
    for (const field of section.fields) {
      snapshots.push({
        field,
        label: formatFieldLabel(field),
        currentValue: readEnhancementControlValue(field),
      })
    }
  }
  return snapshots
}

async function reviewBulkEnhancementChanges(
  mode: BulkEnhancementMode,
): Promise<BulkEnhancementReviewResult> {
  const identity = chromeEnhancementIdentity()
  if (feedType.value !== 'manual') {
    return { status: 'error', message: chromeEnhancementUnavailableMessage }
  }
  if (chromeEnhancementSourcePhase.value === 'error') {
    return { status: 'error', message: chromeEnhancementUnavailableMessage }
  }
  if (chromeEnhancementSourcePhase.value !== 'loaded') {
    await loadChromeEnhancementSource()
  }
  if (chromeEnhancementIdentity() !== identity || chromeEnhancementSourcePhase.value !== 'loaded') {
    return { status: 'error', message: chromeEnhancementUnavailableMessage }
  }

  return {
    status: 'ready',
    identity,
    rows: buildEditVehicleBulkEnhancementReview({
      mode,
      fields: collectVisibleEnhancementFieldSnapshots(),
      sanitizedChromeValues: chromeSourceValues.value,
      dealerTimeZone: dealerTimeZone.value,
    }),
  }
}

function bulkEnhancementFieldCount(count: number): string {
  return `${count} field${count === 1 ? '' : 's'}`
}

function bulkEnhancementApplyMessage(applied: number, failed: number): string {
  if (applied > 0 && failed === 0) return `Applied ${bulkEnhancementFieldCount(applied)}.`
  if (applied > 0) {
    return `Applied ${bulkEnhancementFieldCount(applied)}. ${bulkEnhancementFieldCount(failed)} could not be applied.`
  }
  return `${bulkEnhancementFieldCount(failed)} could not be applied.`
}

async function applyBulkEnhancementChanges(request: BulkEnhancementApplyRequest) {
  if (request.identity !== chromeEnhancementIdentity()) {
    resetEnhanceVehicleDataDialog()
    return
  }

  let applied = 0
  let failed = 0
  for (const field of request.fields) {
    const result = applyEnhancementCandidateToField(field)
    if (result === 'applied') {
      applied += 1
      setEnhancementSourceNotice(field, null)
    } else {
      failed += 1
    }
  }

  resetEnhanceVehicleDataDialog()
  makeAlert(
    bulkEnhancementApplyMessage(applied, failed),
    failed > 0 ? 'warning' : 'success',
  )
}

watch(
  () => chromeEnhancementIdentity(),
  (next, previous) => {
    if (previous && next !== previous) {
      discardChromeEnhancementSource()
      resetEnhanceVehicleDataDialog()
    }
  },
)

// If the user deep-links into /inventory/:vin/edit, the account store can hydrate after mount.
// Watch and load once accountId becomes available.
watch(
  () => accountSettings.accountId,
  async (id) => {
    if (!id) return
    if (isLoadingVehicle.value) {
      await loadVehicle()
    } else if (!vehicleData.value.vin) {
      isLoadingVehicle.value = true
      await loadVehicle()
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  // Prevent leaking object URLs for unsaved selected photos.
  clearNewPhotoPreviews()
})
</script>

<style scoped>
.manual-override-field-container {
  border: 1px solid transparent;
  border-radius: 10px;
}

.manual-override-field-container--unsaved,
.manual-override-field-container--pending {
  border-color: transparent;
}

.edit-vehicle-sections-row {
  width: 100%;
}

.edit-vehicle-header {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}

.edit-vehicle-identity-badges {
  flex: 1 1 auto;
  min-width: 0;
}

.edit-vehicle-header-helper {
  padding-left: 48px;
}

@media (max-width: 599px) {
  .edit-vehicle-header-helper {
    padding-left: 0;
  }
}

.drag-handle {
  cursor: grab;
  user-select: none;
  -webkit-user-drag: element;
}
.drag-chosen .drag-handle {
  cursor: grabbing;
}
.drag-ghost {
  opacity: 0.5;
}

.photo-grid {
  display: grid;
  /* Smaller tiles, consistent wrapping */
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}
@media (min-width: 960px) {
  .photo-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
}

.photo-thumb {
  position: relative;
}
.uploaded-library-photo {
  display: block;
  width: 100%;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 10px;
  background: transparent;
  overflow: hidden;
  cursor: pointer;
}
.uploaded-library-photo--selected {
  border-color: rgb(var(--v-theme-primary));
}
.uploaded-library-photo:focus-visible {
  outline: 2px solid rgba(var(--v-theme-primary), 0.7);
  outline-offset: 2px;
}
.uploaded-library-photo-status {
  position: absolute;
  right: 6px;
  bottom: 6px;
  border-radius: 50%;
  background: rgb(var(--v-theme-surface));
}
.photo-thumb-image {
  cursor: pointer;
}
.photo-thumb-image:focus-visible {
  outline: 2px solid rgba(var(--v-theme-primary), 0.7);
  outline-offset: 2px;
  border-radius: 10px;
}
.photo-thumb :deep(.v-img) {
  /* Force uniform thumbnail height across the grid */
  height: 96px;
}
@media (min-width: 960px) {
  .photo-thumb :deep(.v-img) {
    height: 112px;
  }
}
.photo-thumb :deep(.v-img__img) {
  object-fit: cover;
}
.photo-drag-handle {
  position: absolute;
  top: 8px;
  left: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(255, 255, 255, 0.92);
  border-radius: 10px;
  padding: 4px 6px;
  line-height: 1;
}
.photo-drag-handle:focus {
  outline: 2px solid rgba(var(--v-theme-primary), 0.6);
  outline-offset: 2px;
}
.photo-remove {
  position: absolute;
  top: 8px;
  right: 8px;
}

.photo-preview-card {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.photo-preview-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  /* keep it tappable and readable on top of images */
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}
/* v-dialog is teleported; style must be global */
:global(.photo-preview-dialog) {
  width: 90vw;
  height: 90vh;
  max-width: 90vw;
  max-height: 90vh;
}
.photo-preview-body {
  height: 100%;
  padding: 8px;
}
.photo-preview-stage {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.photo-preview-img {
  display: block;
  /* Render at intrinsic size, but never exceed dialog */
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  margin: 0 auto;
  object-fit: contain;
}

.form-footer-spacer {
  height: 88px;
}
.form-footer-sticky {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.08);
}

.form-footer-inner {
  width: 100%;
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
}
</style>

